import { Component, Input, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl } from '@angular/forms';
import {
  NumberValidatorService,
  NUMERIC_FIELD_CONSTRAINTS,
} from '../../../../shared/services/number-validator/number-validator.service';
import {
  GeneralOutreachFormV2,
  OwnershipRow,
} from '../../models/general-outreach-form-v2';
import { CDFI_INFO } from '../../../../shared/utils/cdfi-info';
import { CommonService } from '../../../../shared/services/common/common.service';
import {
  OutreachFormData,
  OutreachFormType,
} from '../../models/outreach-form-data';
import { GoogleRecaptchaComponent } from '../../../../shared/components/google-recaptcha/google-recaptcha.component';
import { OutreachService } from '../../services/outreach.service';
import { takeUntil, switchMap, map, of, tap } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { AddressService } from '../../../../shared/services/address/address.service';
import {
  State,
  County,
  City,
  Zipcode,
} from '../../../../shared/models/address';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import juice from 'juice';
import { MicrosoftClarityService } from '../../../../shared/services/microsoft-clarity/microsoft-clarity.service';
import { ActivatedRoute } from '@angular/router';
import { BlockBlobClient } from '@azure/storage-blob';
import { AuthService } from '../../../auth/services/auth.service';
import { OutreachChatBotComponent } from '../outreach-chat-bot/outreach-chat-bot.component';
import { ComboboxComponent } from '../../../../shared/components/combobox/combobox.component';

@Component({
  selector: 'app-general-outreach-form-v2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    GoogleRecaptchaComponent,
    TranslateModule,
    OutreachChatBotComponent,
    ComboboxComponent,
  ],
  templateUrl: './general-outreach-form-v2.component.html',
  styleUrl: './general-outreach-form-v2.component.scss',
})
export class GeneralOutreachFormV2Component
  extends DestroyBase
  implements OnInit, OnDestroy
{
  /* eslint-disable @typescript-eslint/no-explicit-any */
  @Input() outreachPrefillData: OutreachFormData | null = null;
  @Input() uniqueId: string | null = null;
  @Input() isDemoMode = false;

  commonService = inject(CommonService);
  outreachService = inject(OutreachService);
  translateService = inject(TranslateService);
  clarityService = inject(MicrosoftClarityService);
  activatedRoute = inject(ActivatedRoute);
  authService = inject(AuthService);
  addressService = inject(AddressService);
  numberValidatorService = inject(NumberValidatorService);

  private readonly STORAGE_KEY = 'generalOutreachFormData';
  private readonly LOAN_APPLICATION_STORAGE_KEY = 'loanApplicationFormData';
  private prefilledEmail: string | null = null;
  currentLanguage = 'en'; // Default language
  cdfiInfo = CDFI_INFO;
  isSubmitted = false;
  isNoInterestSubmitted = false; // Track special submission for "No" to staying informed
  isSubmitting = false; // Track if form is currently being submitted
  submitError = ''; // Store submission error message
  submitFieldErrors: string[] = []; // Store field-specific error messages as a list
  currentSection = 0;
  section0Page = 0; // Track which page of section 0 we're on (0-6)
  formHistory: GeneralOutreachFormV2[] = [];
  sectionHistory: number[] = [0];
  showEmailVerificationPopup = false;
  isEmailVerificationSent = false;
  isEmailVerified = false;
  emailVerificationCode = '';
  emailVerificationError = '';
  isEditingEmail = false;
  tempEmail = '';
  captchaToken: string | null = null;
  isLoanApplication = false;
  resendCountdown = 0;
  canResendEmail = false;
  ref: string | undefined;
  access_type = 'link';
  private resendTimer: any = null;
  matchedConnections: string[] = [];

  // SME signup portal invite state
  smeEmailSent = false;
  smeSignupLoading = false;
  smeSignupError = '';

  // Polling state for matched connections
  isPollingMatches = false;
  pollingMessage = '';
  private pollingInterval: any = null;
  private readonly POLLING_INTERVAL_MS = 3000; // Poll every 3 seconds
  private readonly MAX_POLLING_ATTEMPTS = 40; // Max 2 minutes (40 * 3 seconds)
  private pollingAttempts = 0;

  // File upload properties
  selectedFiles: File[] = [];
  fileUploadError = '';
  fileUploadConsent = false;
  termsOfUseConsent = false;
  showFileUploadProgress = false;
  allUploadsCompleted = false;
  fileUploads: {
    fileName: string;
    fileId: number;
    sasUrl: string;
    progress: number;
    status: 'uploading' | 'completed' | 'error';
    error?: string;
  }[] = [];
  private submittedFormId = '';
  private submittedJobId = 0;

  // Error countdown for rate limiting
  errorCountdown = 0;
  private errorTimer: any = null;

  // Period selection state - tracks radio button selection
  periodMonthsSelection: '6' | '9' | '12' | 'other' | '' = '';

  // Track if chamber/networking question has been answered
  chamberNetworkingAnswered = false;

  // Track if findingInvestors was auto-added by ventureFunding selection
  private findingInvestorsAutoAdded = false;

  // Section collapse state
  sectionExpanded = {
    loansAndCapital: true,
    technicalAssistance: true,
    businessOperationalTools: true,
    commercialRealEstate: true,
    solarAndEnergy: true,
    findingInvestors: true,
  };

  // Form timing for completion tracking
  private formStartTime: number = Date.now();
  private lastInteractedField = '';
  private formOutcomeTracked = false;

  // State and County data
  states: State[] = [];
  availableCounties: County[] = [];
  availableCities: City[] = [];
  availableZipCodes: Zipcode[] = [];

  businessTypeOptions = [
    {
      value: 'sole_proprietorship',
      labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.SOLE_PROPRIETORSHIP',
    },
    { value: 'lp', labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.LP' },
    { value: 'llp', labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.LLP' },
    { value: 'llc', labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.LLC' },
    {
      value: 'c_corporation',
      labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.C_CORPORATION',
    },
    {
      value: 's_corporation',
      labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.S_CORPORATION',
    },
    {
      value: 'benefit_corporation',
      labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.BENEFIT_CORPORATION',
    },
    {
      value: 'close_corporation',
      labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.CLOSE_CORPORATION',
    },
    {
      value: 'non_profit_corporation',
      labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.NON_PROFIT_CORPORATION',
    },
    {
      value: 'cooperative',
      labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.COOPERATIVE',
    },
    {
      value: 'other',
      labelKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.OTHER',
    },
  ];

  form: GeneralOutreachFormV2 = {
    name: '',
    email: '',
    phone: '',
    preferredLanguage: '',
    isMilitaryVeteran: undefined,
    isVeteranSpouse: undefined,
    state: '',
    county: '',
    city: '',
    zipCode: '',
    hasBusiness: undefined,
    businessName: '',
    businessAddress: '',
    businessWebsite: '',
    businessType: '',
    businessEstablished: '',
    numberOfEmployees: undefined,
    hasRegisteredEntity: undefined,
    capitalProductType: [],
    interestedProducts: [],
    wantsDetailedQuestionnaire: undefined,
    willFillPrescreening: undefined,
    businessIndustry: '',
    businessHelpAspects: [],
    workedWithSupportOrg: undefined,
    willingToPayForAssistance: '',
    wantsStartupAssistance: undefined,
    startupHelpAreas: [],
    interestedInSellingBusiness: '',
    interestedInEmployeeStrategies: '',
    interestedInSuccessionPlanning: undefined,
    tools: {
      businessBanking: [],
      marketingHelp: [],
      techSupport: [],
      businessInsurance: [],
      newTechnology: [],
    },
    lookingToRentOrBuy: '',
    lookingToUpgradeFacility: undefined,
    ownFacility: undefined,
    buildingOwnerWillingToSupport: '',
    interestedInEnergyUpgrades: undefined,
    interestedInSolarProviderOrLoan: undefined,
    interestedInEEUpgrades: undefined,
    buildingBuiltYear: undefined,
    buildingImprovements: '',
    applicationSummary: '',
    powerUtilityProviders: '',
    interestedInBatteries: '',
    canProvidePowerUseStatement: undefined,
    hasElectricVehicles: undefined,
    lookingToAddElectricVehicle: undefined,
    knowsInvestorType: '',
    investmentTypes: [],
    willingToRelocate: '',
    ownershipStructure: [
      {
        ownerName: '',
        ownershipPercentage: undefined,
        otherBusinessesPercentage: undefined,
      },
    ],
    primaryOwnerName: '',
    hasOtherBusinessInterests: undefined,
    numberOfOtherBusinesses: undefined,
    ownershipPercentage: undefined,
    loanAmount: undefined,
    loanTermMonths: undefined,
    creditScoreRange: '',
    hasFiledAnnualReport: undefined,
    hasCoSigner: undefined,
    needsTechnicalAssistance: undefined,
    hasBusinessPlan: undefined,
    hasFinancialProjections: undefined,
    wantsToConnect: undefined,
    wantsFormCopy: undefined,
    consentToShare: false,
    optOutCommunications: false,
    contributingPersonalCapital: undefined,
    personalCapitalPercentage: undefined,
    projectDescription: [],
    collateralTypes: [],
    reportingPeriod: '',
    periodMonths: undefined,
    customPeriodMonths: undefined,
    totalRevenue: undefined,
    netProfit: undefined,
    depreciationAmortization: undefined,
    totalAssets: undefined,
    cashEquivalents: undefined,
    receivables: undefined,
    inventoryValue: undefined,
    currentLiabilities: undefined,
    shareholderEquity: undefined,
    alternativeNetWorth: undefined,
    existingLoansAmount: undefined,
    creditLinesLimit: undefined,
    monthlyDebtService: undefined,
    totalPersonalIncome: undefined,
    personalNetWorth: undefined,
    personalLoansAmount: undefined,
    financialDocumentation: [],
    otp: '',
  };
  formSnapshots: any[] = [];
  generatedFormHtml: { html: string } = { html: '' };
  showBusinessAgeEmployeeCountQuestion: boolean | null | undefined = false;

  // Cache variables
  private citiesAndCountiesCache = new Map<
    string,
    { counties: County[]; cities: City[] }
  >();

  private zipcodesCache = new Map<string, Zipcode[]>();

  rating = 0;
  comment = '';

  readonly stars = [1, 2, 3, 4, 5];

  submittingFeedback = false;

  showFeedbackPopup = false;
  feedbackSubmitted = false;
  feedbackError = '';
  private feedbackPopupTimer: any = null;

  onJoinNow(): void {
    const email = this.form.email?.trim();
    if (!email) {
      this.smeSignupError = this.translateService.instant(
        'GENERAL_OUTREACH_FORM.MESSAGES.PORTAL_INVITE_EMAIL_REQUIRED',
      );
      return;
    }
    this.smeSignupLoading = true;
    this.smeSignupError = '';
    this.clarityService.sendClarityEvent('outreach_portal_invite_requested');
    this.authService
      .smeSignup(email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.smeSignupLoading = false;
          this.smeEmailSent = true;
          this.clarityService.sendClarityEvent('outreach_portal_invite_sent');
        },
        error: (err) => {
          this.smeSignupLoading = false;
          this.smeSignupError =
            err?.error?.detail ||
            err?.error?.message ||
            this.translateService.instant(
              'GENERAL_OUTREACH_FORM.MESSAGES.PORTAL_INVITE_SIGNUP_ERROR',
            );
          this.clarityService.sendClarityEvent('outreach_portal_invite_failed');
        },
      });
  }

  setRating(star: number): void {
    this.rating = star;
  }

  submitFeedback(): void {
    if (this.submittingFeedback || !this.submittedFormId) {
      this.feedbackError = this.translateService.instant(
        'GENERAL_OUTREACH_FORM.MESSAGES.FEEDBACK_SUBMIT_ERROR',
      );
      return;
    }

    const payload = {
      rating: this.rating,
      comment: this.comment.trim(),
    };

    this.submittingFeedback = true;
    this.feedbackError = '';

    this.outreachService
      .submitFeedback(payload, this.submittedFormId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.submittingFeedback = false;
          this.feedbackSubmitted = true;
          this.clarityService.trackEvent('outreach_feedback_submitted', {
            outreach_feedback_rating: String(this.rating),
          });
        },
        error: (error) => {
          this.submittingFeedback = false;
          this.feedbackError =
            error?.error?.detail ||
            error?.error?.message ||
            this.translateService.instant(
              'GENERAL_OUTREACH_FORM.MESSAGES.FEEDBACK_SUBMIT_ERROR',
            );
          this.clarityService.sendClarityEvent('outreach_feedback_failed');
        },
      });
  }

  ngOnInit(): void {
    // Initialize translation service
    this.translateService.setDefaultLang('en');

    // Load saved language or default to English
    const savedLanguage = localStorage.getItem('formLanguage') || 'en';
    this.currentLanguage = savedLanguage;
    this.translateService.use(this.currentLanguage);
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.ref = params['ref'];
        this.access_type = params['access_type'];
      });

    this.isLoanApplication =
      this.outreachPrefillData?.outreach_type === OutreachFormType.LOAN;

    // Restore saved snapshots
    const savedSnapshots = localStorage.getItem('formSnapshots');
    if (savedSnapshots) {
      this.formSnapshots = JSON.parse(savedSnapshots);
    }

    // Restore saved snapshots
    if (this.outreachPrefillData?.outreach_type === OutreachFormType.LOAN) {
      this.clearStoredFormData();
      this.isLoanApplication = true;

      const savedFormData = localStorage.getItem(
        this.LOAN_APPLICATION_STORAGE_KEY,
      );
      if (savedFormData) {
        const parsedData = JSON.parse(savedFormData);
        this.form = { ...this.form, ...parsedData };

        // Load saved form start time for loan applications
        const savedStartTime = localStorage.getItem('formStartTime');
        if (savedStartTime) {
          this.formStartTime = parseInt(savedStartTime, 10);
        }
      } else {
        // For new loan applications, pre-select loans and capital product
        this.form.interestedProducts = ['loansAndCapital'];
        this.form.willFillPrescreening = true;
        this.form.wantsDetailedQuestionnaire = true;
        this.form.hasBusiness = 'yes';

        // Save initial form start time for new loan application sessions
        localStorage.setItem('formStartTime', this.formStartTime.toString());
      }
    } else {
      // Normal outreach flow
      this.isLoanApplication = false;
      localStorage.removeItem(this.LOAN_APPLICATION_STORAGE_KEY);

      // Load states
      this.loadStates();

      // Restore saved form
      const savedFormData = localStorage.getItem(this.STORAGE_KEY);
      if (savedFormData) {
        const parsedData = JSON.parse(savedFormData);
        this.form = { ...this.form, ...parsedData };
        this.currentSection = parseInt(
          localStorage.getItem('currentSection') || '0',
          10,
        );
        this.section0Page = parseInt(
          localStorage.getItem('section0Page') || '0',
          10,
        );
        this.sectionHistory = JSON.parse(
          localStorage.getItem('sectionHistory') || '[0]',
        );

        // Load saved form start time or use current time
        const savedStartTime = localStorage.getItem('formStartTime');
        if (savedStartTime) {
          this.formStartTime = parseInt(savedStartTime, 10);
        }

        // Restore cities + zipcodes
        if (this.form.state) {
          const state = this.states.find((s) => s.name === this.form.state);

          if (state) {
            this.getCachedOrFetchCitiesAndCounties([state.id])
              .pipe(
                takeUntil(this.destroy$),
                tap((res) => {
                  this.availableCounties = (res.counties || [])
                    .slice()
                    .sort((a, b) =>
                      a.name.localeCompare(b.name, undefined, {
                        sensitivity: 'base',
                      }),
                    );
                  this.availableCities = (res.cities || [])
                    .slice()
                    .sort((a, b) =>
                      this.formatCityName(a.name).localeCompare(
                        this.formatCityName(b.name),
                        undefined,
                        { sensitivity: 'base' },
                      ),
                    );
                }),
                map((res) => res.cities.find((c) => c.name === this.form.city)),
                switchMap((city) => {
                  if (!city) return of([]);
                  return this.getCachedOrFetchZipcodes([city.id]);
                }),
              )
              .subscribe((zips) => {
                this.availableZipCodes = zips;
              });
          }
        }

        // Restore period selection
        this.initializePeriodMonthsSelection();

        // If we're on page 6 or later in section 0, assume chamber networking question was answered
        if (this.currentSection === 0 && this.section0Page >= 6) {
          this.chamberNetworkingAnswered = true;
        } else if (this.currentSection > 0) {
          this.chamberNetworkingAnswered = true;
        }
      } else {
        // Save initial form start time for new sessions
        localStorage.setItem('formStartTime', this.formStartTime.toString());
      }
      if (this.outreachPrefillData?.email) {
        this.form.email = this.outreachPrefillData.email;
        this.prefilledEmail = this.outreachPrefillData.email;
        this.isEmailVerified = true; // Auto-verify prefilled email
      }
    }

    // Track the actual restored/new step rather than the component defaults.
    this.initializeClarityTags();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    // Track form abandonment if not submitted
    if (!this.formOutcomeTracked) {
      this.trackFormCompletion('abandoned');
    }

    // Clear form data from localStorage when component is destroyed and form is submitted
    if (this.isSubmitted) {
      this.clearStoredFormData();
    }

    // Clear the resend timer
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }

    // Clear the error timer
    if (this.errorTimer) {
      clearInterval(this.errorTimer);
    }

    // Clear the polling interval
    this.stopPollingMatchedConnections();

    // Clear the feedback popup timer
    if (this.feedbackPopupTimer) {
      clearTimeout(this.feedbackPopupTimer);
    }
  }

  showFeedbackPopupAfterDelay(delayMs = 1500): void {
    if (this.feedbackPopupTimer) {
      clearTimeout(this.feedbackPopupTimer);
    }
    this.feedbackPopupTimer = setTimeout(() => {
      this.showFeedbackPopup = true;
    }, delayMs);
  }

  closeFeedbackPopup(): void {
    this.showFeedbackPopup = false;
    if (this.feedbackPopupTimer) {
      clearTimeout(this.feedbackPopupTimer);
      this.feedbackPopupTimer = null;
    }
  }

  saveFormData(): void {
    // Clear any submission errors when form data is modified
    if (this.submitError) {
      this.submitError = '';
      this.submitFieldErrors = [];
      // Reset email verification state when clearing submission errors
      // but preserve OTP if email is verified
      if (!this.isEmailVerified) {
        this.resetEmailVerificationState();
      }
    }

    const storageKey = this.isLoanApplication
      ? this.LOAN_APPLICATION_STORAGE_KEY
      : this.STORAGE_KEY;
    try {
      localStorage.setItem(storageKey, JSON.stringify(this.form));
      localStorage.setItem('currentSection', this.currentSection.toString());
      localStorage.setItem('section0Page', this.section0Page.toString());
      localStorage.setItem(
        'sectionHistory',
        JSON.stringify(this.sectionHistory),
      );
      localStorage.setItem('formStartTime', this.formStartTime.toString());
    } catch (e) {
      console.warn('Could not save form data to localStorage:', e);
    }
  }

  private clearStoredFormData(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem('currentSection');
    localStorage.removeItem('section0Page');
    localStorage.removeItem('sectionHistory');
    localStorage.removeItem('formLanguage');
    localStorage.removeItem('formStartTime');
    localStorage.removeItem('formSnapshots'); // Clear form snapshots
  }

  // Microsoft Clarity tracking methods
  private initializeClarityTags(): void {
    const storageKey = this.isLoanApplication
      ? this.LOAN_APPLICATION_STORAGE_KEY
      : this.STORAGE_KEY;
    const isResumed = localStorage.getItem(storageKey) !== null;

    this.clarityService.trackEvent('outreach_form_opened', {
      experience: 'public_outreach',
      outreach_form_type: this.getClarityFormType(),
      outreach_language: this.currentLanguage,
      outreach_entry: isResumed ? 'resumed' : 'new',
    });
    this.trackCurrentStep('initial');
  }

  private trackCurrentStep(direction: 'initial' | 'forward' | 'back'): void {
    const tags: Record<string, string | string[]> = {
      outreach_step: this.getCurrentSectionName(),
      outreach_navigation: direction,
    };

    if (this.form.interestedProducts.length > 0) {
      tags['outreach_products'] = this.form.interestedProducts;
    }
    if (this.form.hasBusiness) {
      tags['outreach_business_status'] = this.form.hasBusiness;
    }
    if (this.form.state) {
      tags['outreach_state'] = this.form.state;
    }

    this.clarityService.trackEvent('outreach_step_viewed', tags);
  }

  private getClarityFormType(): string {
    switch (this.outreachPrefillData?.outreach_type) {
      case OutreachFormType.LOAN:
        return 'loan_application';
      case OutreachFormType.GENERIC:
        return 'generic_outreach';
      case OutreachFormType.PUBLIC:
        return 'public_outreach';
      default:
        return 'general_outreach';
    }
  }

  private trackSubmissionAttempt(): void {
    this.clarityService.trackEvent('outreach_form_submit_attempted', {
      outreach_form_type: this.getClarityFormType(),
      outreach_step: this.getCurrentSectionName(),
    });
  }

  private trackSubmissionFailure(status?: number): void {
    let errorType = 'unknown';
    if (status === 400 || status === 422) {
      errorType = 'validation';
    } else if (status === 401 || status === 403) {
      errorType = 'verification_or_authorization';
    } else if (status === 404) {
      errorType = 'not_found';
    } else if (status === 429) {
      errorType = 'rate_limited';
    } else if (status && status >= 500) {
      errorType = 'server';
    } else if (status === 0) {
      errorType = 'network';
    }

    this.clarityService.trackEvent('outreach_form_submit_failed', {
      outreach_error_type: errorType,
      outreach_step: this.getCurrentSectionName(),
    });
  }

  // Get human-readable section name
  private getCurrentSectionName(): string {
    if (this.currentSection === 0) {
      const basicSteps = [
        'products_of_interest',
        'business_status',
        'assistance_summary',
        'location',
        'business_details',
        'preferences',
        'contact_and_followup',
      ];
      return basicSteps[this.section0Page] || 'basic_information';
    }

    const currentProductSection = this.getCurrentProductSection();
    if (currentProductSection) {
      return currentProductSection;
    }

    // Final consent section
    return 'final_consent';
  }

  private calculateFormCompletionPercentage(): number {
    const requiredFields = [
      'name',
      'email',
      'state',
      'county',
      'city',
      'zipCode',
      'preferredLanguage',
      'isMilitaryVeteran',
      'isVeteranSpouse',
    ];

    let filledFields = 0;
    let totalFields = requiredFields.length;

    // Count basic required fields
    for (const field of requiredFields) {
      const value = this.form[field as keyof GeneralOutreachFormV2];
      if (value !== undefined && value !== null && value !== '') {
        filledFields++;
      }
    }

    // Add loan-specific fields if in loan section
    if (this.currentSection >= 2 || this.isLoanApplication) {
      totalFields += 2; // loanAmount, loanTermMonths
      if (this.form.loanAmount) filledFields++;
      if (this.form.loanTermMonths) filledFields++;
    }

    return Math.round((filledFields / totalFields) * 100);
  }

  private trackFormCompletion(
    outcome: 'submitted' | 'abandoned' | 'no_interest',
  ): void {
    if (this.formOutcomeTracked) {
      return;
    }
    this.formOutcomeTracked = true;

    const elapsedSeconds = Math.max(
      0,
      Math.floor((Date.now() - this.formStartTime) / 1000),
    );
    const completionPercentage = this.calculateFormCompletionPercentage();
    const tags: Record<string, string | string[]> = {
      outreach_outcome: outcome,
      outreach_last_step: this.getCurrentSectionName(),
      outreach_completion_band:
        this.getCompletionPercentageBand(completionPercentage),
      outreach_elapsed_time: this.getElapsedTimeBand(elapsedSeconds),
    };

    if (this.form.interestedProducts.length > 0) {
      tags['outreach_products'] = this.form.interestedProducts;
    }
    if (outcome === 'abandoned' && this.lastInteractedField) {
      tags['outreach_drop_off_field'] = this.lastInteractedField;
    }

    const eventName =
      outcome === 'submitted'
        ? 'outreach_form_submitted'
        : outcome === 'no_interest'
          ? 'outreach_form_no_interest'
          : 'outreach_form_abandoned';
    this.clarityService.trackEvent(eventName, tags);
  }

  private getCompletionPercentageBand(percentage: number): string {
    if (percentage >= 100) return '100';
    if (percentage >= 75) return '75-99';
    if (percentage >= 50) return '50-74';
    if (percentage >= 25) return '25-49';
    return '0-24';
  }

  private getElapsedTimeBand(seconds: number): string {
    if (seconds < 120) return 'under_2m';
    if (seconds < 300) return '2_to_5m';
    if (seconds < 600) return '5_to_10m';
    if (seconds < 1200) return '10_to_20m';
    return '20m_plus';
  }

  // Retain only the final focused field; send it once if the form is abandoned.
  trackFieldInteraction(fieldName: string): void {
    this.lastInteractedField = fieldName;
  }

  // Helper method for business age calculation (used internally, not tracked in Clarity)
  private calculateBusinessAge(establishedDate: string): number | null {
    try {
      const established = new Date(establishedDate);
      const now = new Date();
      const ageInYears = Math.floor(
        (now.getTime() - established.getTime()) /
          (1000 * 60 * 60 * 24 * 365.25),
      );
      return ageInYears >= 0 ? ageInYears : null;
    } catch {
      return null;
    }
  }

  onBack(): void {
    // Clear any submission errors when navigating back
    this.submitError = '';
    this.submitFieldErrors = [];

    // Reset email verification state when going back
    this.resetEmailVerificationState();

    // If we're in section 0 and not on the first page, go to previous section 0 page
    if (this.currentSection === 0 && this.section0Page > 0) {
      this.section0Page--;

      // Skip Page 4 (business details) if user doesn't have a business when going back
      if (this.section0Page === 4 && this.form.hasBusiness !== 'yes') {
        this.section0Page--; // Skip back to Page 3
      }

      // Reset chamber networking answer flag if going back to before page 6
      if (this.section0Page < 6) {
        this.chamberNetworkingAnswered = false;
      }

      this.saveFormData();
      this.trackCurrentStep('back');
      this.scrollToTop();
      return;
    }

    // Otherwise, navigate to previous section
    if (this.sectionHistory.length > 1) {
      // First save current form state before going back
      this.formHistory[this.currentSection] = { ...this.form };

      // Remove current section from history
      this.sectionHistory.pop();
      // Get previous section
      this.currentSection = this.sectionHistory[this.sectionHistory.length - 1];

      // Reset section0Page when leaving section 0
      if (this.currentSection === 0) {
        this.section0Page = 6; // Go to last page of section 0
      }

      // Don't merge previous section data to avoid overriding current form state
      // The form state should persist as is when navigating between sections

      this.saveFormData();
      this.trackCurrentStep('back');
      this.scrollToTop();
    }
  }

  // Reset email verification state
  private resetEmailVerificationState(): void {
    this.isEmailVerified = false;
    this.isEmailVerificationSent = false;
    // Only clear verification code if not already verified and stored in form
    if (!this.form.otp) {
      this.emailVerificationCode = '';
    }
    this.emailVerificationError = '';
    this.isEditingEmail = false;
    this.tempEmail = '';
    this.canResendEmail = false;
    this.resendCountdown = 0;
    this.captchaToken = null;

    // Clear the resend timer
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }

    // Clear the error timer and reset countdown
    if (this.errorTimer) {
      clearInterval(this.errorTimer);
      this.errorTimer = null;
    }
    this.errorCountdown = 0;
  }

  navigateToSection(nextSection: number): void {
    // Clear any submission errors when navigating
    this.submitError = '';
    this.submitFieldErrors = [];

    // Reset email verification state when navigating between sections
    this.resetEmailVerificationState();

    // Save current state before navigating
    this.formHistory[this.currentSection] = { ...this.form };
    this.currentSection = nextSection;
    this.sectionHistory.push(nextSection);

    // Reset section0Page when entering section 0
    if (nextSection === 0) {
      this.section0Page = 0;
    }

    // Initialize ownership structure when navigating to section 2 (loan prescreening)
    if (
      nextSection === 2 &&
      (!this.form.ownershipStructure ||
        this.form.ownershipStructure.length === 0)
    ) {
      this.form.ownershipStructure = [
        {
          ownerName: '',
          ownershipPercentage: undefined,
          otherBusinessesPercentage: undefined,
        },
      ];
    }

    // Don't merge previous section data to avoid overriding current form state
    // The form state should persist as is when navigating between sections

    this.saveFormData();
    this.trackCurrentStep('forward');
    this.scrollToTop();
  }

  // Navigate back to the last section with form data
  navigateToLastSection(preserveError = false): void {
    const lastSectionIndex = this.sectionHistory.length - 1;
    if (lastSectionIndex >= 0) {
      this.currentSection = this.sectionHistory[lastSectionIndex];
    } else {
      // If no section history, determine last section based on form state
      this.currentSection = this.getLastValidSection();
    }

    // Reset email verification state when navigating to last section
    this.resetEmailVerificationState();

    // Only save form data if we're not preserving error state
    // When preserving error, we want to keep submitError intact
    if (!preserveError) {
      this.saveFormData();
    } else {
      // Save form data without clearing submit error
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.form));
      localStorage.setItem('currentSection', this.currentSection.toString());
      localStorage.setItem(
        'sectionHistory',
        JSON.stringify(this.sectionHistory),
      );
    }
    this.scrollToTop();
  }

  // Determine the last valid section based on the form's navigation path
  private getLastValidSection(): number {
    // Check which sections the user should have reached based on their answers
    if (this.form.wantsDetailedQuestionnaire === true) {
      if (this.form.willFillPrescreening === true) {
        return 3; // Alternative Info section
      } else {
        return 1; // Existing Business section
      }
    } else {
      return 0; // Basic section with consent
    }
  }

  isEmailValid(): boolean {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return !this.form.email || emailPattern.test(this.form.email);
  }

  isPhoneValid(): boolean {
    const phonePattern = /^\+?[\d\s-()]{10,}$/;
    return !this.form.phone || phonePattern.test(this.form.phone);
  }

  isZipCodeValid(): boolean {
    const zipPattern = /^\d{5}(-\d{4})?$/;
    return !this.form.zipCode || zipPattern.test(this.form.zipCode);
  }

  isWebsiteValid(): boolean {
    if (!this.form.businessWebsite) return true;
    // Allow various URL formats without requiring https://
    const urlPattern =
      /^(https?:\/\/)?([\da-z.-]+)\.([\da-z.]{2,6})([/\w .-]*)*\/?$/i;
    return urlPattern.test(this.form.businessWebsite);
  }

  isOwnershipPercentageValid(): boolean {
    if (!this.form.ownershipPercentage && this.form.ownershipPercentage !== 0)
      return true;
    return (
      this.form.ownershipPercentage >= 0 && this.form.ownershipPercentage <= 100
    );
  }

  isPersonalCapitalPercentageValid(): boolean {
    return this.validateFieldWithConstraint(
      'personalCapitalPercentage',
      this.form.personalCapitalPercentage,
    );
  }

  isLoanTermMonthsValid(): boolean {
    return this.validateFieldWithConstraint(
      'loanTermMonths',
      this.form.loanTermMonths,
    );
  }

  isNumberOfEmployeesValid(): boolean {
    return this.validateFieldWithConstraint(
      'numberOfEmployees',
      this.form.numberOfEmployees,
    );
  }

  restrictIntegerInput(event: KeyboardEvent): void {
    if (['.', ',', 'e', 'E', '-', '+'].includes(event.key)) {
      event.preventDefault();
    }
  }

  onNumberOfEmployeesChange(value: any): void {
    if (value !== undefined && value !== null && value !== '') {
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue) && parsedValue % 1 !== 0) {
        setTimeout(() => {
          this.form.numberOfEmployees = Math.trunc(parsedValue);
          this.saveFormData();
        });
        return;
      }
    }
    this.saveFormData();
  }

  validateFieldWithConstraint(fieldName: string, value: any): boolean {
    const FIELD_TO_CONSTRAINT_KEY_MAP: Record<string, string> = {
      numberOfEmployees: 'numberOfEmployees',
      loanAmount: 'requestingLoanAmount',
      loanTermMonths: 'loanTermMonths',
      personalCapitalPercentage: 'personalCapitalPercentage',
      periodMonths: 'monthsInPeriod',
      customPeriodMonths: 'monthsInPeriod',
      totalRevenue: 'totalRevenue',
      netProfit: 'netProfit',
      depreciationAmortization: 'depreciation',
      totalAssets: 'totalAssets',
      cashEquivalents: 'cashEquivalents',
      receivables: 'tradeReceivables',
      inventoryValue: 'inventoryValue',
      currentLiabilities: 'totalCurrentLiabilities',
      shareholderEquity: 'shareholderEquity',
      alternativeNetWorth: 'alternativeNetWorth',
      existingLoansAmount: 'outstandingLoans',
      creditLinesLimit: 'approvedCreditLimit',
      monthlyDebtService: 'monthlyDebtService',
      totalPersonalIncome: 'personalIncome',
      personalNetWorth: 'personalNetWorth',
      personalLoansAmount: 'personalLoans',
    };

    const constraintKey = FIELD_TO_CONSTRAINT_KEY_MAP[fieldName] || fieldName;
    const constraint = NUMERIC_FIELD_CONSTRAINTS[constraintKey];
    if (!constraint) {
      return true;
    }
    if (value === undefined || value === null || value === '') {
      return true;
    }
    const control = new FormControl(value);
    const validator =
      this.numberValidatorService.numberValidator(constraintKey);
    const errors = validator(control);
    return errors === null;
  }

  getFinancialFieldError(fieldName: string): string {
    const value = (this.form as any)[fieldName];
    const FIELD_TO_CONSTRAINT_KEY_MAP: Record<string, string> = {
      numberOfEmployees: 'numberOfEmployees',
      loanAmount: 'requestingLoanAmount',
      loanTermMonths: 'loanTermMonths',
      personalCapitalPercentage: 'personalCapitalPercentage',
      periodMonths: 'monthsInPeriod',
      customPeriodMonths: 'monthsInPeriod',
      totalRevenue: 'totalRevenue',
      netProfit: 'netProfit',
      depreciationAmortization: 'depreciation',
      totalAssets: 'totalAssets',
      cashEquivalents: 'cashEquivalents',
      receivables: 'tradeReceivables',
      inventoryValue: 'inventoryValue',
      currentLiabilities: 'totalCurrentLiabilities',
      shareholderEquity: 'shareholderEquity',
      alternativeNetWorth: 'alternativeNetWorth',
      existingLoansAmount: 'outstandingLoans',
      creditLinesLimit: 'approvedCreditLimit',
      monthlyDebtService: 'monthlyDebtService',
      totalPersonalIncome: 'personalIncome',
      personalNetWorth: 'personalNetWorth',
      personalLoansAmount: 'personalLoans',
    };

    const constraintKey = FIELD_TO_CONSTRAINT_KEY_MAP[fieldName] || fieldName;
    const constraint = NUMERIC_FIELD_CONSTRAINTS[constraintKey];
    if (!constraint) {
      return 'GENERAL_OUTREACH_FORM.VALIDATION.INVALID_FINANCIAL_FIELD';
    }
    if (value === undefined || value === null || value === '') {
      return '';
    }
    const control = new FormControl(value);
    const validator =
      this.numberValidatorService.numberValidator(constraintKey);
    const errors = validator(control);
    if (errors) {
      if (errors['decimalPlaces'] || errors['pattern']) {
        return 'GENERAL_OUTREACH_FORM.VALIDATION.INVALID_DECIMAL_PLACES';
      }
    }
    return 'GENERAL_OUTREACH_FORM.VALIDATION.INVALID_FINANCIAL_FIELD';
  }

  onWebsiteChange(): void {
    // Save form data when website option changes
    this.saveFormData();
  }

  // Validate section 0 based on current page
  isBasicSectionValid(): boolean {
    switch (this.section0Page) {
      case 0: // Page 0: What are you looking for?
        return this.hasSelectedProduct();

      case 1: // Page 1: Do you have a business?
        return this.form.hasBusiness !== undefined;

      case 2: // Page 2: Application Summary (What help do you need?)
        return (
          this.form.applicationSummary !== undefined &&
          this.form.applicationSummary.trim() !== ''
        );

      case 3: // Page 3: Location (state, county, city, zipCode, businessAddress)
        if (this.form.hasBusiness === 'yes') {
          return (
            this.form.state !== '' &&
            this.form.county !== '' &&
            this.form.city !== '' &&
            this.form.zipCode !== '' &&
            this.isZipCodeValid()
          );
        } else {
          return (
            this.form.state !== '' &&
            this.form.county !== '' &&
            this.form.city !== '' &&
            this.form.zipCode !== '' &&
            this.isZipCodeValid() &&
            this.form.businessAddress !== undefined &&
            this.form.businessAddress.trim() !== ''
          );
        }

      case 4: // Page 4: Business details (only if hasBusiness === 'yes')
        // Skip this page if user doesn't have a business
        if (this.form.hasBusiness !== 'yes') {
          return true;
        }
        // Validate business fields if user has a business
        return (
          this.form.businessName !== '' &&
          this.form.businessAddress !== undefined &&
          this.form.businessAddress.trim() !== '' &&
          this.form.businessType !== '' &&
          this.form.businessEstablished !== '' &&
          this.isBusinessEstablishedValid() &&
          this.isWebsiteValid() &&
          this.form.numberOfEmployees !== undefined &&
          this.form.numberOfEmployees !== null &&
          this.isNumberOfEmployeesValid()
        );

      case 5: // Page 5: Language and military
        return (
          this.form.preferredLanguage !== '' &&
          this.form.isMilitaryVeteran !== undefined &&
          this.form.isVeteranSpouse !== undefined
        );

      case 6: // Page 6: Name, email, phone, business networking, detailed questionnaire
        return (
          this.form.name !== '' &&
          this.form.email !== '' &&
          this.isEmailValid() &&
          (!this.form.phone || this.isPhoneValid()) &&
          this.form.wantsToConnect !== undefined &&
          this.form.wantsDetailedQuestionnaire !== undefined
        );

      default:
        return false;
    }
  }

  // Check if at least one product is selected
  hasSelectedProduct(): boolean {
    return this.form.interestedProducts.length > 0;
  }

  // Validation for Section 1 (Existing Business)
  isSection1Valid(): boolean {
    // Check required basic fields
    const basicRequiredValid =
      this.form.businessEstablished !== undefined &&
      this.form.businessEstablished !== '' &&
      this.isBusinessEstablishedValid() &&
      this.form.numberOfEmployees !== undefined &&
      this.form.numberOfEmployees !== null &&
      this.form.hasRegisteredEntity !== undefined &&
      this.form.willFillPrescreening !== undefined;

    return basicRequiredValid;
  }

  onSubmitLoanApplication(): void {
    // Save current form data
    localStorage.setItem(
      this.LOAN_APPLICATION_STORAGE_KEY,
      JSON.stringify(this.form),
    );
    this.submitError = '';
    this.submitFieldErrors = [];
    this.isSubmitting = true;
    this.trackSubmissionAttempt();

    // Helper function to convert camelCase to snake_case
    const convertToSnakeCase = (str: string): string => {
      return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    };

    // Transform ownership structure to have snake_case keys
    const transformedOwnershipStructure = this.form.ownershipStructure?.map(
      (row) => ({
        owner_name: row.ownerName,
        ownership_percentage: row.ownershipPercentage,
        other_businesses_percentage: row.otherBusinessesPercentage,
      }),
    );

    // Create form data with only loan prescreening fields - using snake_case for backend
    const payload: any = {
      // Prescreening form fields
      ownership_structure: transformedOwnershipStructure,
      project_description: this.form.projectDescription.map(convertToSnakeCase),
      contributing_personal_capital: this.form.contributingPersonalCapital,
      personal_capital_percentage: this.form.personalCapitalPercentage,
      collateral_types: this.form.collateralTypes.map(convertToSnakeCase),
      reporting_period: this.form.reportingPeriod,
      period_months: this.form.periodMonths,
      total_revenue: this.form.totalRevenue,
      net_profit: this.form.netProfit,
      depreciation_amortization: this.form.depreciationAmortization,
      total_assets: this.form.totalAssets,
      cash_equivalents: this.form.cashEquivalents,
      receivables: this.form.receivables,
      inventory_value: this.form.inventoryValue,
      current_liabilities: this.form.currentLiabilities,
      shareholder_equity: this.form.shareholderEquity,
      alternative_net_worth: this.form.alternativeNetWorth,
      existing_loans_amount: this.form.existingLoansAmount,
      credit_lines_limit: this.form.creditLinesLimit,
      monthly_debt_service: this.form.monthlyDebtService,
      total_personal_income: this.form.totalPersonalIncome,
      personal_net_worth: this.form.personalNetWorth,
      personal_loans_amount: this.form.personalLoansAmount,
      ref: this.ref,
      access_type: this.access_type,
    };

    // console.log('Submitting loan application with payload:', payload);
    // return;

    this.outreachService
      .submitForm(this.uniqueId!, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.submittedFormId = res?.form_id || this.uniqueId || '';
          this.submittedJobId = res?.job_id || 0;
          this.isSubmitted = true;
          this.isSubmitting = false;
          this.showFeedbackPopupAfterDelay();

          // Track successful loan application completion
          this.trackFormCompletion('submitted');

          localStorage.removeItem(this.LOAN_APPLICATION_STORAGE_KEY);
          this.clearStoredFormData();
        },
        error: (error) => {
          console.error('Error submitting form:', error);
          this.isSubmitting = false;
          this.trackSubmissionFailure(error?.status);

          if (error.status === 500) {
            this.submitError =
              'An unexpected error occurred while submitting the form. Please try again later.';
            return;
          } else if (error.status === 404) {
            this.submitError = 'Invalid form ID.';
            return;
          }

          const fieldErrors: string[] = [];
          const processedFields = new Set<string>(); // Avoid duplicate error messages

          for (const key in error.error) {
            const formKey = this.convertBackendFieldToFormField(key);

            // Avoid duplicate error messages for the same field
            if (processedFields.has(formKey)) {
              continue;
            }
            processedFields.add(formKey);

            const humanReadableField = this.getFieldDisplayName(formKey);
            if (humanReadableField) {
              fieldErrors.push(`"${humanReadableField}" entry is invalid`);
            } else {
              // Fallback for unmapped fields
              const fallbackName = this.formatFieldNameAsFallback(formKey);
              fieldErrors.push(`"${fallbackName}" entry is invalid`);
            }
          }

          if (fieldErrors.length > 0) {
            // Store field errors as an array for list display
            this.submitFieldErrors = fieldErrors;
            this.submitError =
              'Please correct the following fields and try again:';
          } else {
            this.submitError = 'Please check your form entries and try again.';
            this.submitFieldErrors = [];
          }
        },
      });
  }

  onSubmit(): void {
    // Save current form data before any navigation
    this.saveFormData();
    try {
      const snapshot = this.getFormSnapshot();
      const savedSnapshots = JSON.parse(
        localStorage.getItem('formSnapshots') || '{}',
      );

      if (this.currentSection === 0) {
        // This should rarely be hit since we navigate via onNextPage()
        // But keep it for safety in case of direct navigation
        const snapshotKey = `0_${this.section0Page}`;
        savedSnapshots[snapshotKey] = snapshot;
        localStorage.setItem('formSnapshots', JSON.stringify(savedSnapshots));
      } else if (this.currentSection >= 1) {
        // For product sections (section 1+), use direct section index
        savedSnapshots[this.currentSection] = snapshot;
        localStorage.setItem('formSnapshots', JSON.stringify(savedSnapshots));
      }
    } catch (e) {
      console.warn('Could not save form snapshot to localStorage:', e);
    }

    if (this.currentSection === 0) {
      if (!this.isBasicSectionValid()) {
        return; // Don't proceed if validation fails
      }

      if (this.form.wantsDetailedQuestionnaire) {
        // Navigate to first business section (will show common questions + first selected section)
        this.navigateToSection(1);
      } else {
        // Skip to final consent section
        this.navigateToSection(3);
      }
    } else if (this.currentSection >= 1) {
      // Handle navigation for business product sections
      if (this.hasMoreProductSections()) {
        // Move to next product section
        this.navigateToSection(this.currentSection + 1);
      } else if (
        this.form.willFillPrescreening &&
        this.currentSection === 1 &&
        this.form.interestedProducts.includes('loansAndCapital') &&
        !(
          this.form.capitalProductType.length === 1 &&
          this.form.capitalProductType.includes('grant_programs')
        )
      ) {
        // Only navigate to prescreening if on first section, loans selected, and not grant programs
        this.navigateToSection(this.getSelectedSections().length + 1);
      } else {
        // No more sections, go to final consent
        const totalProductSections = this.getSelectedSections().length;
        this.navigateToSection(totalProductSections + 1);
      }
    }
  }

  // Show email verification popup without sending verification immediately
  submitForm(): void {
    // Validate current section before allowing submission
    if (!this.isCurrentSectionValid()) {
      return;
    }

    this.trackSubmissionAttempt();

    // Clear term loan specific fields if we're on section 2 and loan type is working capital
    if (this.currentSection === 2) {
      this.clearTermLoanFieldsIfWorkingCapital();
    }

    // Save snapshot of current section before assembling form copy HTML
    let savedSnapshots: any = {};
    try {
      const currentSnapshot = this.getFormSnapshot();
      savedSnapshots = JSON.parse(
        localStorage.getItem('formSnapshots') || '{}',
      );
      if (this.currentSection === 0) {
        savedSnapshots[`0_${this.section0Page}`] = currentSnapshot;
      } else if (this.currentSection >= 1) {
        savedSnapshots[this.currentSection] = currentSnapshot;
      }
    } catch (e) {
      console.warn('Could not read/save form snapshots:', e);
    }

    // Separate section 0 page snapshots from other section snapshots
    const section0PageSnapshots: any[] = [];
    const otherSectionSnapshots: any[] = [];

    // Extract section 0 page snapshots (keys like "0_0", "0_1", etc.)
    Object.keys(savedSnapshots).forEach((key) => {
      if (key.startsWith('0_')) {
        const pageNum = parseInt(key.split('_')[1]);
        if (!isNaN(pageNum) && savedSnapshots[key]) {
          section0PageSnapshots[pageNum] = savedSnapshots[key];
        }
      } else {
        const sectionNum = parseInt(key);
        if (!isNaN(sectionNum) && sectionNum >= 1 && savedSnapshots[key]) {
          otherSectionSnapshots[sectionNum] = savedSnapshots[key];
        }
      }
    });

    // Combine all snapshots in order: section 0 pages first, then other sections
    // Filter out undefined/null entries but preserve order
    const allSnapshots = [
      ...section0PageSnapshots.filter((snap) => snap && snap.html),
      ...otherSectionSnapshots.filter((snap) => snap && snap.html),
    ];

    this.generatedFormHtml = {
      html: this.generateFormHtml(allSnapshots).html,
    };
    localStorage.removeItem('formSnapshots');

    if (this.outreachPrefillData?.outreach_type === OutreachFormType.PUBLIC) {
      if (!this.isEmailVerified) {
        // Clear any previous OTP when showing verification popup
        this.emailVerificationCode = '';
        this.form.otp = '';
        this.showEmailVerificationPopup = true;
        this.captchaToken = null; // Reset captcha token for popup
        return;
      } else {
        this.form.otp = this.emailVerificationCode;
      }
    }

    this.submitFormWithData();
  }

  private scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const formContainer = document.querySelector('.form-container');
      if (formContainer) {
        formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  // Navigate to next page within section 0 or next section
  onNextPage(): void {
    if (this.currentSection === 0 && this.section0Page <= 6) {
      // Save snapshot of current page before moving to next page
      try {
        const snapshot = this.getFormSnapshot();
        const savedSnapshots = JSON.parse(
          localStorage.getItem('formSnapshots') || '{}',
        );
        // Use a unique key for each page within section 0: "0_pageNumber"
        const snapshotKey = `0_${this.section0Page}`;
        savedSnapshots[snapshotKey] = snapshot;
        localStorage.setItem('formSnapshots', JSON.stringify(savedSnapshots));
      } catch (e: any) {
        console.warn('Could not save form snapshot to localStorage:', e);
        // alert(`[Mobile Debug] Storage warning: ${e?.message || e}`);
      }

      // If this is the last page of section 0, navigate to next section
      if (this.section0Page === 6) {
        this.navigateBasedOnCurrentSection();
        this.scrollToTop();
        return;
      }

      this.section0Page++;

      // Skip Page 4 (business details) if user doesn't have a business
      if (this.section0Page === 4 && this.form.hasBusiness !== 'yes') {
        this.section0Page++; // Skip to Page 5
      }

      this.saveFormData();
      this.trackCurrentStep('forward');
      this.scrollToTop();
    } else {
      // Proceed to next section as normal
      this.navigateBasedOnCurrentSection();
      this.scrollToTop();
    }
  }

  // Getter for read-only business address line 2 displayed on Page 4
  get businessAddressLine2(): string {
    const parts = [
      this.form.city,
      this.form.county,
      this.form.state,
      this.form.zipCode,
    ].filter((part) => part && part.trim() !== '');
    return parts.join(', ');
  }

  // Auto-fill business address with location data from Page 3
  private autoFillBusinessAddress(): void {
    // Only auto-fill if businessAddress is empty
    if (
      !this.form.businessAddress &&
      this.form.city &&
      this.form.state &&
      this.form.zipCode
    ) {
      const addressParts = [
        this.form.city,
        this.form.county,
        this.form.state,
        this.form.zipCode,
      ].filter((part) => part); // Remove empty parts

      this.form.businessAddress = addressParts.join(', ');
      this.saveFormData();
    }
  }

  // Navigate based on current section and form answers
  private navigateBasedOnCurrentSection(): void {
    if (this.currentSection === 0) {
      // After completing section 0, proceed to first product section or final consent
      if (this.form.wantsDetailedQuestionnaire === true) {
        this.navigateToSection(1);
      } else {
        // User doesn't want detailed questionnaire - show final consent
        this.currentSection = -1; // Use -1 to show final consent without product sections
        this.sectionHistory.push(-1);
        this.saveFormData();
        this.trackCurrentStep('forward');
      }
    }
  }

  // Check if current section is valid
  isCurrentSectionValid(): boolean {
    // For loan applications, use special validation
    if (this.isLoanApplication) {
      return this.validateLoanApplicationForm();
    }

    if (this.currentSection === 0) {
      return this.isBasicSectionValid();
    }

    // For product sections (currentSection >= 1)
    if (this.currentSection >= 1) {
      const currentProductSection = this.getCurrentProductSection();
      if (!currentProductSection) {
        return true; // No section to validate
      }

      // Validate common business questions on first product page (currentSection === 1)
      if (this.currentSection === 1) {
        const commonQuestionsValid = this.validateCommonBusinessQuestions();
        if (!commonQuestionsValid) {
          return false;
        }
      }

      // Validate the specific product section
      return this.validateProductSection(currentProductSection);
    }

    return true;
  }

  // Validate loan application form (only prescreening fields)
  private validateLoanApplicationForm(): boolean {
    // Validate ownership structure (at least first row must be filled)
    if (
      !this.form.ownershipStructure ||
      this.form.ownershipStructure.length === 0 ||
      !this.form.ownershipStructure[0].ownerName ||
      this.form.ownershipStructure[0].ownerName.trim() === '' ||
      this.form.ownershipStructure[0].ownershipPercentage === undefined ||
      this.form.ownershipStructure[0].ownershipPercentage === null
    ) {
      return false;
    }

    // Validate project description (at least one option selected)
    if (this.form.projectDescription.length === 0) {
      return false;
    }

    // Validate contributingPersonalCapital
    if (this.form.contributingPersonalCapital === undefined) {
      return false;
    }

    // If contributing personal capital, validate percentage
    if (
      this.form.contributingPersonalCapital === true &&
      (this.form.personalCapitalPercentage === undefined ||
        this.form.personalCapitalPercentage === null ||
        this.form.personalCapitalPercentage < 0 ||
        this.form.personalCapitalPercentage > 100)
    ) {
      return false;
    }

    // Validate collateral types (at least one option selected)
    if (this.form.collateralTypes.length === 0) {
      return false;
    }

    // Validate financial information
    if (!this.form.reportingPeriod) {
      return false;
    }
    if (
      this.form.periodMonths === undefined ||
      this.form.periodMonths === null
    ) {
      return false;
    }

    if (!this.form.totalRevenue) {
      return false;
    }
    if (
      this.form.totalRevenue === undefined ||
      this.form.totalRevenue === null
    ) {
      return false;
    }

    return true;
  }

  // Validate common business questions shown on page 2 (first product section page)
  private validateCommonBusinessQuestions(): boolean {
    // Do you have a business?
    if (!this.form.hasBusiness) {
      return false;
    }

    // If they have a business, check conditional fields
    if (this.form.hasBusiness === 'yes') {
      if (!this.form.businessName || this.form.businessName.trim() === '') {
        return false;
      }
      if (
        !this.form.businessAddress ||
        this.form.businessAddress.trim() === ''
      ) {
        return false;
      }
      if (!this.form.businessType || this.form.businessType.trim() === '') {
        return false;
      }
      if (
        !this.form.businessEstablished ||
        this.form.businessEstablished.trim() === ''
      ) {
        return false;
      }
      if (
        this.form.numberOfEmployees === undefined ||
        this.form.numberOfEmployees === null
      ) {
        return false;
      }
    }

    return true;
  }

  // Validate specific product section
  private validateProductSection(sectionName: string): boolean {
    switch (sectionName) {
      case 'loansAndCapital':
        return this.validateLoansAndCapitalSection();
      case 'technicalAssistance':
        return this.validateTechnicalAssistanceSection();
      case 'businessOperationalTools':
        return this.validateBusinessToolsSection();
      case 'commercialRealEstate':
        return this.validateCommercialRealEstateSection();
      case 'solarAndEnergy':
        return this.validateSolarAndEnergySection();
      case 'findingInvestors':
        return this.validateFindingInvestorsSection();
      default:
        return true;
    }
  }

  private validateLoansAndCapitalSection(): boolean {
    if (this.form.hasRegisteredEntity === undefined) {
      return false;
    }
    if (
      this.form.loanAmount === undefined ||
      this.form.loanAmount === null ||
      !this.validateFieldWithConstraint('loanAmount', this.form.loanAmount)
    ) {
      return false;
    }
    if (
      this.form.loanTermMonths === undefined ||
      this.form.loanTermMonths === null ||
      (this.form.loanTermMonths as any) === '' ||
      !this.isLoanTermMonthsValid()
    ) {
      return false;
    }

    if (
      !this.form.capitalProductType ||
      this.form.capitalProductType.length === 0
    ) {
      return false;
    }
    // Check if user selected only grant_programs
    const hasOnlyGrantPrograms =
      this.form.capitalProductType.length === 1 &&
      this.form.capitalProductType.includes('grant_programs');
    // If not only grant programs, prescreening question is required
    if (!hasOnlyGrantPrograms && this.form.willFillPrescreening === undefined) {
      return false;
    }
    // If user said No to prescreening, validate additional fields
    if (!hasOnlyGrantPrograms && this.form.willFillPrescreening === false) {
      if (!this.form.creditScoreRange) {
        return false;
      }
      if (this.form.hasCoSigner === undefined) {
        return false;
      }
      if (!this.form.hasBusinessPlan) {
        return false;
      }
      if (!this.form.hasFinancialProjections) {
        return false;
      }
      // Check that at least one financial documentation option is selected
      const hasFinancialDoc = this.form.financialDocumentation.length > 0;
      if (!hasFinancialDoc) {
        return false;
      }
    }
    // If user said Yes to prescreening, validate prescreening form fields
    if (!hasOnlyGrantPrograms && this.form.willFillPrescreening === true) {
      // Validate ownership structure (at least first row must be filled including otherBusinessesPercentage)
      if (
        !this.form.ownershipStructure ||
        this.form.ownershipStructure.length === 0 ||
        !this.form.ownershipStructure[0].ownerName ||
        this.form.ownershipStructure[0].ownerName.trim() === '' ||
        this.form.ownershipStructure[0].ownershipPercentage === undefined ||
        this.form.ownershipStructure[0].ownershipPercentage === null ||
        this.form.ownershipStructure[0].otherBusinessesPercentage ===
          undefined ||
        this.form.ownershipStructure[0].otherBusinessesPercentage === null
      ) {
        return false;
      }

      // Validate project description (at least one option selected)
      if (this.form.projectDescription.length === 0) {
        return false;
      }

      // Validate contributingPersonalCapital
      if (this.form.contributingPersonalCapital === undefined) {
        return false;
      }

      // If contributing personal capital, validate percentage
      if (
        this.form.contributingPersonalCapital === true &&
        (this.form.personalCapitalPercentage === undefined ||
          this.form.personalCapitalPercentage === null ||
          (this.form.personalCapitalPercentage as any) === '' ||
          !this.isPersonalCapitalPercentageValid())
      ) {
        return false;
      }

      // Validate collateral types (at least one option selected)
      if (this.form.collateralTypes.length === 0) {
        return false;
      }

      // Validate financial information
      if (!this.form.reportingPeriod) {
        return false;
      }
      if (
        this.form.periodMonths === undefined ||
        this.form.periodMonths === null
      ) {
        return false;
      }

      // Validate financial documentation (at least one option selected)
      const hasFinancialDoc = this.form.financialDocumentation.length > 0;
      if (!hasFinancialDoc) {
        return false;
      }

      // Check all financial fields validation
      const financialFields = [
        'totalRevenue',
        'netProfit',
        'depreciationAmortization',
        'totalAssets',
        'cashEquivalents',
        'receivables',
        'inventoryValue',
        'currentLiabilities',
        'shareholderEquity',
        'alternativeNetWorth',
        'existingLoansAmount',
        'creditLinesLimit',
        'monthlyDebtService',
        'totalPersonalIncome',
        'personalNetWorth',
        'personalLoansAmount',
      ];
      for (const field of financialFields) {
        if (!this.isValidFinancialField(field)) {
          return false;
        }
      }
    }

    if (!this.form.totalRevenue) {
      return false;
    }
    if (
      this.form.totalRevenue === undefined ||
      this.form.totalRevenue === null
    ) {
      return false;
    }

    if (!this.isValidFinancialField('totalRevenue')) {
      return false;
    }
    return true;
  }

  private validateTechnicalAssistanceSection(): boolean {
    // Validation depends on whether user has a business
    if (this.form.hasBusiness === 'yes') {
      // For existing business owners
      if (!this.form.businessIndustry || this.form.businessIndustry === '') {
        return false;
      }
      // Validate at least one business help aspect is selected
      if (this.form.businessHelpAspects.length === 0) {
        return false;
      }
      // If business succession is selected, validate related questions
      if (this.form.businessHelpAspects.includes('businessSuccession')) {
        if (!this.form.interestedInSellingBusiness) {
          return false;
        }
        if (!this.form.interestedInEmployeeStrategies) {
          return false;
        }
        if (this.form.interestedInSuccessionPlanning === undefined) {
          return false;
        }
      }
    } else if (this.form.hasBusiness === 'planning') {
      // For users who don't have a business - validate wantsStartupAssistance
      if (this.form.wantsStartupAssistance === undefined) {
        return false;
      }
    } else if (this.form.hasBusiness === 'no') {
      // For users who want to start a business - validate at least one startup help area
      if (this.form.startupHelpAreas.length === 0) {
        return false;
      }
    }

    // Common validation for all users
    if (this.form.workedWithSupportOrg === undefined) {
      return false;
    }
    if (!this.form.willingToPayForAssistance) {
      return false;
    }
    return true;
  }

  private validateBusinessToolsSection(): boolean {
    // At least one business tool should be selected
    const tools = this.form.tools;
    return (
      tools.businessBanking.length > 0 ||
      tools.marketingHelp.length > 0 ||
      tools.techSupport.length > 0 ||
      tools.businessInsurance.length > 0 ||
      tools.newTechnology.length > 0
    );
  }

  private validateCommercialRealEstateSection(): boolean {
    // Check required fields
    if (!this.form.lookingToRentOrBuy) {
      return false;
    }
    if (this.form.lookingToUpgradeFacility === undefined) {
      return false;
    }

    // If looking to upgrade facility, check nested questions
    if (this.form.lookingToUpgradeFacility === true) {
      // Must answer if they own the facility
      if (this.form.ownFacility === undefined) {
        return false;
      }

      // If they don't own the facility, must answer if building owner is willing to support
      if (this.form.ownFacility === false) {
        if (!this.form.buildingOwnerWillingToSupport) {
          return false;
        }
      }
    }

    // Check energy upgrades question
    if (this.form.interestedInEnergyUpgrades === undefined) {
      return false;
    }

    return true;
  }

  private validateSolarAndEnergySection(): boolean {
    if (this.form.interestedInSolarProviderOrLoan === undefined) {
      return false;
    }
    if (this.form.interestedInEEUpgrades === undefined) {
      return false;
    }
    if (
      this.form.buildingBuiltYear === undefined ||
      this.form.buildingBuiltYear === null ||
      this.form.buildingBuiltYear === '' ||
      !this.isBuildingBuiltYearValid()
    ) {
      return false;
    }
    if (!this.form.interestedInBatteries) {
      return false;
    }
    if (this.form.canProvidePowerUseStatement === undefined) {
      return false;
    }
    if (this.form.hasElectricVehicles === undefined) {
      return false;
    }
    if (this.form.lookingToAddElectricVehicle === undefined) {
      return false;
    }
    // Validate credit score range if it should be shown
    if (
      this.shouldShowCreditScoreInSolarSection() &&
      !this.form.creditScoreRange
    ) {
      return false;
    }
    return true;
  }

  private validateFindingInvestorsSection(): boolean {
    if (!this.form.knowsInvestorType) {
      return false;
    }
    // If they know the investor type, check that at least one investment type is selected
    if (this.form.knowsInvestorType === 'yes') {
      if (this.form.investmentTypes.length === 0) {
        return false;
      }
    }
    if (!this.form.willingToRelocate) {
      return false;
    }
    return true;
  }
  // Actual form submission after email verification
  private submitFormWithData(): void {
    // Clear any previous error messages
    this.submitError = '';
    this.submitFieldErrors = [];
    this.isSubmitting = true;

    if (this.outreachPrefillData?.outreach_type === OutreachFormType.PUBLIC) {
      // Close email verification popup
      this.closeEmailVerificationPopup();
    }

    // Add file names to attachments array
    if (this.selectedFiles.length > 0) {
      this.form.attachments = this.selectedFiles.map((file) => file.name);
    }

    // Filter form data to only include relevant sections based on the navigation path
    const filteredFormData = this.filterFormDataByNavigationPath();

    // Add HTML representation of the filled form if user wants a copy
    if (this.form.wantsFormCopy) {
      (filteredFormData as any).formHtml = this.generatedFormHtml.html;
      this.generatedFormHtml = { html: '' };
    }

    // Transform loans and capital fields into nested object
    const transformedFormData =
      this.transformLoansAndCapitalFields(filteredFormData);

    // Convert to snake_case for backend
    const snakeCasePayload = this.convertToSnakeCase(transformedFormData);

    // Use the OTP from the form (which should be set during verification)
    // Fallback to component property if not set in form
    if (!snakeCasePayload.otp && this.emailVerificationCode) {
      snakeCasePayload.otp = this.emailVerificationCode;
    } else if (
      this.outreachPrefillData?.outreach_type === OutreachFormType.GENERIC
    ) {
      delete snakeCasePayload.otp;
    }

    if (this.uniqueId && this.uniqueId === 'test-general') {
      console.log('Form submitted:', snakeCasePayload);
      this.isSubmitted = true;
      this.isSubmitting = false;
      this.showFeedbackPopupAfterDelay();

      // Track successful form completion
      this.trackFormCompletion('submitted');

      this.clearStoredFormData();
      return;
    } else {
      snakeCasePayload.ref = this.ref;
      snakeCasePayload.access_type = this.access_type;
      this.outreachService
        .submitForm(this.uniqueId!, snakeCasePayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.submittedFormId = res.form_id;
            this.submittedJobId = res.job_id;
            this.isSubmitting = false;

            // Track successful form completion
            this.trackFormCompletion('submitted');

            this.clearStoredFormData();

            // Check if there are files to upload
            if (this.selectedFiles.length > 0 && res.sas_urls?.length > 0) {
              // Prepare file uploads with SAS URLs
              this.fileUploads = res.sas_urls.map((sasFile) => ({
                fileName: sasFile.file_name,
                fileId: sasFile.id,
                sasUrl: sasFile.sas_url,
                progress: 0,
                status: 'uploading' as const,
              }));

              // Show upload progress modal
              this.showFileUploadProgress = true;

              // Start uploading files to Azure
              this.uploadFilesToAzure();
            } else {
              // No files to upload, show success and start polling
              this.isSubmitted = true;
              this.showFeedbackPopupAfterDelay();
              this.startPollingMatchedConnections();
            }
          },
          error: (error) => {
            console.error('Error submitting form:', error);
            this.isSubmitting = false;
            this.trackSubmissionFailure(error?.status);

            if (error.status === 401 && error.error?.detail === 'Invalid OTP') {
              // Clear previous OTP when showing popup for invalid OTP
              this.emailVerificationCode = '';
              this.form.otp = '';
              this.emailVerificationError =
                'Invalid OTP code. Please try again.';
              this.showEmailVerificationPopup = true;
              this.isEmailVerificationSent = true;
              return;
            }

            if (error.status === 500) {
              // Handle server error
              this.submitError =
                'An unexpected error occurred while submitting the form. Please try again later.';
              return;
            }

            if (error.error?.detail) {
              // Handle specific error message from backend
              this.submitError = error.error.detail;
              return;
            }

            const fieldErrors: string[] = [];
            const processedFields = new Set<string>(); // Avoid duplicate error messages

            for (const key in error.error) {
              const formKey = this.convertBackendFieldToFormField(key);

              // Avoid duplicate error messages for the same field
              if (processedFields.has(formKey)) {
                continue;
              }
              processedFields.add(formKey);

              const humanReadableField = this.getFieldDisplayName(formKey);
              if (humanReadableField) {
                fieldErrors.push(`"${humanReadableField}" entry is invalid`);
              } else {
                // Fallback for unmapped fields
                const fallbackName = this.formatFieldNameAsFallback(formKey);
                fieldErrors.push(`"${fallbackName}" entry is invalid`);
              }
            }

            if (fieldErrors.length > 0) {
              // Store field errors as an array for list display
              this.submitFieldErrors = fieldErrors;
              this.submitError =
                'Please correct the following fields and try again:';
            } else {
              this.submitError =
                'Please check your form entries and try again.';
              this.submitFieldErrors = [];
            }

            // Navigate back to the last section of the form to show the error
            this.navigateToLastSection(true);
          },
        });
    }
  }

  // Transform product-specific fields into nested objects
  private transformLoansAndCapitalFields(form: any): any {
    const transformedForm = { ...form };

    // Check which products the user selected BEFORE any processing
    const selectedLoansAndCapital =
      transformedForm.interestedProducts?.includes('loansAndCapital') === true;
    const selectedTechnicalAssistance =
      transformedForm.interestedProducts?.includes('technicalAssistance') ===
      true;
    const selectedBusinessTools =
      transformedForm.interestedProducts?.includes(
        'businessOperationalTools',
      ) === true;
    const selectedCommercialRealEstate =
      transformedForm.interestedProducts?.includes('commercialRealEstate') ===
      true;
    const selectedSolarAndEnergy =
      transformedForm.interestedProducts?.includes('solarAndEnergy') === true;
    const selectedFindingInvestors =
      transformedForm.interestedProducts?.includes('findingInvestors') === true;

    // Define the fields that should be nested under loansAndCapital
    const loansAndCapitalFields = [
      'hasRegisteredEntity',
      'capitalProductType',
      'willFillPrescreening',
      'ownershipStructure',
      'primaryOwnerName',
      'hasOtherBusinessInterests',
      'numberOfOtherBusinesses',
      'ownershipPercentage',
      'loanAmount',
      'loanTermMonths',
      'projectDescription',
      'contributingPersonalCapital',
      'personalCapitalPercentage',
      'collateralTypes',
      'reportingPeriod',
      'periodMonths',
      'totalRevenue',
      'netProfit',
      'depreciationAmortization',
      'totalAssets',
      'cashEquivalents',
      'receivables',
      'inventoryValue',
      'currentLiabilities',
      'shareholderEquity',
      'alternativeNetWorth',
      'existingLoansAmount',
      'creditLinesLimit',
      'monthlyDebtService',
      'totalPersonalIncome',
      'personalNetWorth',
      'personalLoansAmount',
      'financialDocumentation',
      // 'creditScoreRange' - removed, now a common property used by both loansAndCapital and solarAndEnergy
      'hasFiledAnnualReport',
      'hasCoSigner',
      'needsTechnicalAssistance',
      'hasBusinessPlan',
      'hasFinancialProjections',
    ];

    // Define the fields that should be nested under technicalAssistance
    const technicalAssistanceFields = [
      'businessIndustry',
      'businessHelpAspects',
      'workedWithSupportOrg',
      'willingToPayForAssistance',
      'interestedInSellingBusiness',
      'interestedInEmployeeStrategies',
      'interestedInSuccessionPlanning',
      'wantsStartupAssistance',
      'startupHelpAreas',
    ];

    // Define the fields that should be nested under businessOperationalTools
    const businessToolsFields = ['tools'];

    // Define the fields that should be nested under commercialRealEstate
    const commercialRealEstateFields = [
      'lookingToRentOrBuy',
      'lookingToUpgradeFacility',
      'ownFacility',
      'buildingOwnerWillingToSupport',
      'interestedInEnergyUpgrades',
    ];

    // Define the fields that should be nested under solarAndEnergy
    const solarAndEnergyFields = [
      'interestedInSolarProviderOrLoan',
      'interestedInEEUpgrades',
      'buildingBuiltYear',
      'buildingImprovements',
      'powerUtilityProviders',
      'interestedInBatteries',
      'canProvidePowerUseStatement',
      'hasElectricVehicles',
      'lookingToAddElectricVehicle',
    ];

    // Define the fields that should be nested under findingInvestors
    const findingInvestorsFields = [
      'knowsInvestorType',
      'investmentTypes',
      'willingToRelocate',
    ];

    // Helper function to check if a value is meaningful and should be included
    const hasMeaningfulValue = (value: any): boolean => {
      if (value === undefined || value === null || value === '') {
        return false;
      }

      // For objects, check if they have any true/meaningful values (recursively for nested objects)
      if (typeof value === 'object' && !Array.isArray(value)) {
        return Object.values(value).some((v) => {
          // Recursively check nested objects
          if (typeof v === 'object' && !Array.isArray(v)) {
            return hasMeaningfulValue(v);
          }
          // Arrays are meaningful if they have elements
          if (Array.isArray(v)) {
            return v.length > 0;
          }
          return (
            v === true ||
            (typeof v === 'number' && v !== 0) ||
            (typeof v === 'string' && v !== '')
          );
        });
      }

      // For arrays, check if they have elements
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      // For primitive values, it's meaningful
      return true;
    };

    // Helper function to create nested object from field list
    const createNestedObject = (fields: string[]): any => {
      const nestedObj: any = {};

      fields.forEach((field) => {
        if (field in transformedForm) {
          const value = transformedForm[field];

          if (hasMeaningfulValue(value)) {
            nestedObj[field] = value;
          }

          // Remove from root level
          delete transformedForm[field];
        }
      });

      return nestedObj;
    };

    // Create the nested loansAndCapital object (only if user selected it)
    if (selectedLoansAndCapital) {
      const loansAndCapital = createNestedObject(loansAndCapitalFields);
      if (Object.keys(loansAndCapital).length > 0) {
        transformedForm.loansAndCapital = loansAndCapital;
      }
    }

    // Create the nested technicalAssistance object (only if user selected it)
    if (selectedTechnicalAssistance) {
      const technicalAssistance = createNestedObject(technicalAssistanceFields);

      // Remove startupHelpAreas if hasBusiness is not 'no'
      if (
        transformedForm.hasBusiness !== 'no' &&
        'startupHelpAreas' in technicalAssistance
      ) {
        delete technicalAssistance.startupHelpAreas;
      }

      // Remove wantsStartupAssistance if hasBusiness is not 'planning'
      if (
        transformedForm.hasBusiness !== 'planning' &&
        'wantsStartupAssistance' in technicalAssistance
      ) {
        delete technicalAssistance.wantsStartupAssistance;
      }

      if (Object.keys(technicalAssistance).length > 0) {
        transformedForm.technicalAssistance = technicalAssistance;
      }
    }

    // Create the nested businessOperationalTools object (only if user selected it)
    if (selectedBusinessTools) {
      const businessOperationalTools = createNestedObject(businessToolsFields);
      if (Object.keys(businessOperationalTools).length > 0) {
        transformedForm.businessOperationalTools = businessOperationalTools;
      }
    }

    // Create the nested commercialRealEstate object (only if user selected it)
    if (selectedCommercialRealEstate) {
      const commercialRealEstate = createNestedObject(
        commercialRealEstateFields,
      );
      if (Object.keys(commercialRealEstate).length > 0) {
        transformedForm.commercialRealEstate = commercialRealEstate;
      }
    }

    // Create the nested solarAndEnergy object (only if user selected it)
    if (selectedSolarAndEnergy) {
      const solarAndEnergy = createNestedObject(solarAndEnergyFields);
      if (Object.keys(solarAndEnergy).length > 0) {
        transformedForm.solarAndEnergy = solarAndEnergy;
      }
    }

    // Create the nested findingInvestors object (only if user selected it)
    if (selectedFindingInvestors) {
      const findingInvestors = createNestedObject(findingInvestorsFields);
      if (Object.keys(findingInvestors).length > 0) {
        transformedForm.findingInvestors = findingInvestors;
      }
    }

    // customPeriodMonths is only used for UI selection; do not send in payload
    delete (transformedForm as any).customPeriodMonths;

    return transformedForm;
  }

  // Transform checkbox objects to string arrays for submission

  // Convert object keys from camelCase to snake_case for backend
  private convertToSnakeCase(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => {
        // Convert string items from camelCase to snake_case
        if (typeof item === 'string') {
          return item.replace(/([A-Z])/g, '_$1').toLowerCase();
        }
        return this.convertToSnakeCase(item);
      });
    }

    if (typeof obj === 'object' && obj.constructor === Object) {
      const snakeCaseObj: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          // Convert key from camelCase to snake_case
          const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          let value = this.convertToSnakeCase(obj[key]);

          // Convert preferred_language value to lowercase
          if (snakeKey === 'preferred_language' && typeof value === 'string') {
            value = value.toLowerCase();
          }

          // Don't convert attachment file names - preserve them as-is
          if (snakeKey === 'attachments' && Array.isArray(value)) {
            value = obj[key]; // Use original array without conversion
          }

          snakeCaseObj[snakeKey] = value;
        }
      }
      return snakeCaseObj;
    }

    return obj;
  }

  startEditingEmail(): void {
    this.isEditingEmail = true;
    this.tempEmail = this.form.email;
  }
  updateEmail(): void {
    if (this.isTempEmailValid()) {
      this.form.email = this.tempEmail;
      this.isEditingEmail = false;
      this.isEmailVerified = false;
      this.isEmailVerificationSent = false;
      this.emailVerificationCode = '';
      this.form.otp = ''; // Clear OTP when email changes
      this.canResendEmail = false;
      this.resendCountdown = 0;
      this.captchaToken = null; // Reset captcha token for new email

      // Clear any existing timer
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }

      // Clear any existing error timer
      if (this.errorTimer) {
        clearInterval(this.errorTimer);
        this.errorTimer = null;
      }

      // Reset error countdown and clear error message
      this.errorCountdown = 0;
      this.emailVerificationError = '';
    }
  }

  cancelEditingEmail(): void {
    this.isEditingEmail = false;
    this.tempEmail = '';
  }

  sendEmailVerification(): void {
    if (this.isEmailValid() && this.captchaToken != null) {
      // Clear previous OTP when sending new verification
      this.emailVerificationCode = '';
      this.form.otp = '';
      this.emailVerificationError = '';
      this.commonService
        .verifyEmail(this.form.email, this.captchaToken)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isEmailVerificationSent = true;
            this.startResendTimer();
            this.clarityService.sendClarityEvent(
              'outreach_verification_code_sent',
            );
          },
          error: (err) => {
            console.error('Error sending email verification:', err);
            this.clarityService.trackEvent(
              'outreach_verification_send_failed',
              {
                outreach_error_type:
                  err?.status === 429 ? 'rate_limited' : 'request_failed',
              },
            );
            if (err.status === 429) {
              this.startErrorCountdown();
            } else {
              this.emailVerificationError =
                'Failed to send verification email. Please try again later.';
            }
          },
        });
    }
  }

  private startResendTimer(): void {
    this.resendCountdown = 45;
    this.canResendEmail = false;

    // Clear any existing timer
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }

    this.resendTimer = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.canResendEmail = true;
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    }, 1000);
  }

  private startErrorCountdown(): void {
    this.errorCountdown = 40;
    this.emailVerificationError = `OTP limit exceeded. Please try again in ${this.errorCountdown} seconds.`;

    // Clear any existing error timer
    if (this.errorTimer) {
      clearInterval(this.errorTimer);
    }

    this.errorTimer = setInterval(() => {
      this.errorCountdown--;
      if (this.errorCountdown > 0) {
        this.emailVerificationError = `OTP limit exceeded. Please try again in ${this.errorCountdown} seconds.`;
      } else {
        this.emailVerificationError = '';
        clearInterval(this.errorTimer);
        this.errorTimer = null;
      }
    }, 1000);
  }

  resendVerificationEmail(): void {
    if (this.canResendEmail) {
      this.sendEmailVerification();
    }
  }
  verifyEmailCode(): void {
    if (this.emailVerificationCode && this.isOtpValid()) {
      this.emailVerificationError = '';
      this.isEmailVerified = true;
      this.showEmailVerificationPopup = false;

      // Clear the resend timer
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      this.canResendEmail = false;
      this.resendCountdown = 0;

      // Store the verified OTP code in the form before submission
      this.form.otp = this.emailVerificationCode;
      this.clarityService.sendClarityEvent('outreach_verification_submitted');

      this.submitFormWithData();
    } else {
      this.emailVerificationError =
        'Please enter a valid 6-digit verification code.';
    }
  }

  onConsentChange(field: 'consentToShare' | 'optOutCommunications'): void {
    if (field === 'consentToShare' && this.form.consentToShare) {
      this.form.optOutCommunications = false;
    } else if (
      field === 'optOutCommunications' &&
      this.form.optOutCommunications
    ) {
      this.form.consentToShare = false;
    }
  }

  onChamberNetworkingAnswer(): void {
    this.chamberNetworkingAnswered = true;
  }

  onChamberNetworkingYes(): void {
    if (!this.form.interestedProducts.includes('chamber_or_networking')) {
      this.form.interestedProducts.push('chamber_or_networking');
    }
    this.form.wantsToConnect = true;
    this.onChamberNetworkingAnswer();
    this.saveFormData();
  }

  onChamberNetworkingNo(): void {
    const index = this.form.interestedProducts.indexOf('chamber_or_networking');
    if (index > -1) {
      this.form.interestedProducts.splice(index, 1);
    }
    this.form.wantsToConnect = false;
    this.onChamberNetworkingAnswer();
    this.saveFormData();
  }

  isConsentSelected(): boolean {
    return this.form.consentToShare || this.form.optOutCommunications;
  }

  isFormReadyToSubmit(): boolean {
    // Check if files are selected and consent is required
    const fileConsentValid =
      this.selectedFiles.length === 0 ||
      (this.fileUploadConsent && this.termsOfUseConsent);

    const hasApplicationSummary = !!this.form.applicationSummary?.trim();

    // For users who answered "No" to detailed questionnaire (on section 0)
    if (
      this.currentSection === 0 &&
      this.form.wantsDetailedQuestionnaire === false
    ) {
      return (
        this.isBasicSectionValid() &&
        hasApplicationSummary &&
        this.isConsentSelected() &&
        fileConsentValid &&
        !this.isSubmitting
      );
    }

    // For users completing the full form
    return (
      this.isCurrentSectionValid() &&
      hasApplicationSummary &&
      this.form.wantsToConnect !== undefined &&
      this.form.wantsFormCopy !== undefined &&
      this.isConsentSelected() &&
      fileConsentValid &&
      !this.isSubmitting
    );
  }

  // New method to check if basic required fields are filled for "No interest" submission
  isNoInterestFormReady(): boolean {
    const basicValid =
      this.form.name !== '' &&
      this.form.email !== '' &&
      this.isEmailValid() &&
      (!this.form.phone || this.isPhoneValid()) &&
      this.form.preferredLanguage !== '' &&
      this.form.isMilitaryVeteran !== undefined &&
      this.form.isVeteranSpouse !== undefined &&
      this.form.state !== '' &&
      this.form.city !== '' &&
      this.form.zipCode !== '' &&
      this.isZipCodeValid() &&
      this.hasSelectedProduct();

    return basicValid;
  }

  onCaptchaTokenEmit(token: string): void {
    this.captchaToken = token;
  }

  // Handle captcha token specifically for email verification popup
  onPopupCaptchaTokenEmit(token: string): void {
    this.captchaToken = token;
  }

  // Check if Get OTP button should be enabled
  isGetOtpEnabled(): boolean {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    const isFormEmailValid =
      this.form.email && emailPattern.test(this.form.email);
    const isNotInErrorCountdown = this.errorCountdown <= 0;
    return (
      !!isFormEmailValid && this.captchaToken !== null && isNotInErrorCountdown
    );
  }

  // Check if temp email is valid during editing
  isTempEmailValid(): boolean {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return !this.tempEmail || emailPattern.test(this.tempEmail);
  }

  // Check if OTP/email verification code is valid (exactly 6 digits)
  isOtpValid(): boolean {
    if (!this.emailVerificationCode) return false;
    const otpPattern = /^\d{6}$/;
    return otpPattern.test(this.emailVerificationCode);
  }

  // Check if OTP has more than 6 digits
  hasOtpExceededLimit(): boolean {
    if (!this.emailVerificationCode) return false;
    return this.emailVerificationCode.length > 6;
  }

  // Get appropriate OTP error message
  getOtpErrorMessage(): string {
    if (!this.emailVerificationCode) return '';
    if (this.emailVerificationCode.length > 6) {
      return 'Verification code cannot be more than 6 digits.';
    }
    if (this.emailVerificationCode.length < 6) {
      return 'Please enter a valid 6-digit verification code.';
    }
    if (!/^\d+$/.test(this.emailVerificationCode)) {
      return 'Verification code must contain only numbers.';
    }
    return 'Please enter a valid 6-digit verification code.';
  }

  // Handle OTP input
  onOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input && input.value) {
      // Allow user to type more than 6 digits to show error, but don't slice
      this.emailVerificationCode = input.value;
    }
  }

  // Method for submitting when user answers "No" to staying informed
  submitNoInterestForm(): void {
    const filteredFormData = this.filterFormDataByNavigationPath();
    console.log('Form submitted with no interest:', filteredFormData);

    // Track no interest submission as a completed form
    this.trackFormCompletion('no_interest');

    this.isNoInterestSubmitted = true;
    this.clearStoredFormData();
  }

  // Method to restart the form
  restartForm(): void {
    this.isNoInterestSubmitted = false;
    this.isSubmitted = false;
    this.isSubmitting = false;
    this.submitError = '';
    this.submitFieldErrors = [];
    this.currentSection = 0;
    this.section0Page = 0;
    this.sectionHistory = [0];
    this.formOutcomeTracked = false;

    // Reset form start time for new session
    this.formStartTime = Date.now();

    // Reset email verification state
    this.resetEmailVerificationState();

    // Reset period months selection
    this.periodMonthsSelection = '';

    // Reset findingInvestors auto-added flag
    this.findingInvestorsAutoAdded = false;

    // Reset file upload state
    this.selectedFiles = [];
    this.fileUploadError = '';
    this.fileUploadConsent = false;
    this.termsOfUseConsent = false;

    this.form = {
      name: '',
      email: '',
      phone: '',
      preferredLanguage: '',
      isMilitaryVeteran: undefined,
      isVeteranSpouse: undefined,
      state: '',
      county: '',
      city: '',
      zipCode: '',
      hasBusiness: undefined,
      businessName: '',
      businessAddress: '',
      businessWebsite: '',
      businessType: '',
      businessEstablished: '',
      numberOfEmployees: undefined,
      hasRegisteredEntity: undefined,
      capitalProductType: [],
      interestedProducts: [],
      wantsDetailedQuestionnaire: undefined,
      willFillPrescreening: undefined,
      businessIndustry: '',
      businessHelpAspects: [],
      workedWithSupportOrg: undefined,
      willingToPayForAssistance: '',
      wantsStartupAssistance: undefined,
      startupHelpAreas: [],
      interestedInSellingBusiness: '',
      interestedInEmployeeStrategies: '',
      interestedInSuccessionPlanning: undefined,
      tools: {
        businessBanking: [],
        marketingHelp: [],
        techSupport: [],
        businessInsurance: [],
        newTechnology: [],
      },
      lookingToRentOrBuy: '',
      lookingToUpgradeFacility: undefined,
      ownFacility: undefined,
      buildingOwnerWillingToSupport: '',
      interestedInEnergyUpgrades: undefined,
      interestedInSolarProviderOrLoan: undefined,
      interestedInEEUpgrades: undefined,
      buildingBuiltYear: undefined,
      buildingImprovements: '',
      applicationSummary: '',
      powerUtilityProviders: '',
      interestedInBatteries: '',
      canProvidePowerUseStatement: undefined,
      hasElectricVehicles: undefined,
      lookingToAddElectricVehicle: undefined,
      knowsInvestorType: '',
      investmentTypes: [],
      willingToRelocate: '',
      ownershipStructure: [
        {
          ownerName: '',
          ownershipPercentage: undefined,
          otherBusinessesPercentage: undefined,
        },
      ],
      primaryOwnerName: '',
      hasOtherBusinessInterests: undefined,
      numberOfOtherBusinesses: undefined,
      ownershipPercentage: undefined,
      loanAmount: undefined,
      loanTermMonths: undefined,
      creditScoreRange: '',
      hasFiledAnnualReport: undefined,
      hasCoSigner: undefined,
      needsTechnicalAssistance: undefined,
      hasBusinessPlan: undefined,
      hasFinancialProjections: undefined,
      wantsToConnect: undefined,
      wantsFormCopy: undefined,
      consentToShare: false,
      optOutCommunications: false,
      contributingPersonalCapital: undefined,
      personalCapitalPercentage: undefined,
      projectDescription: [],
      collateralTypes: [],
      reportingPeriod: '',
      periodMonths: undefined,
      customPeriodMonths: undefined,
      totalRevenue: undefined,
      netProfit: undefined,
      depreciationAmortization: undefined,
      totalAssets: undefined,
      cashEquivalents: undefined,
      receivables: undefined,
      inventoryValue: undefined,
      currentLiabilities: undefined,
      shareholderEquity: undefined,
      alternativeNetWorth: undefined,
      existingLoansAmount: undefined,
      creditLinesLimit: undefined,
      monthlyDebtService: undefined,
      totalPersonalIncome: undefined,
      personalNetWorth: undefined,
      personalLoansAmount: undefined,
      financialDocumentation: [],
      otp: '',
    };
    this.clearStoredFormData();
    this.clarityService.sendClarityEvent('outreach_form_restarted');
    this.trackCurrentStep('initial');
    this.scrollToTop();
  }

  closeEmailVerificationPopup(): void {
    this.showEmailVerificationPopup = false;
    // Use the reset method to handle all email verification state
    this.resetEmailVerificationState();
  }

  // Handle changes to willFillPrescreening
  onWillFillPrescreeningChange(): void {
    if (this.form.willFillPrescreening === false) {
      // Clear prescreening fields (shown when willFillPrescreening === true)
      this.form.ownershipStructure = [
        {
          ownerName: '',
          ownershipPercentage: undefined,
          otherBusinessesPercentage: undefined,
        },
      ];
      this.form.primaryOwnerName = '';
      this.form.hasOtherBusinessInterests = undefined;
      this.form.numberOfOtherBusinesses = undefined;
      this.form.ownershipPercentage = undefined;

      // Clear project description
      this.form.projectDescription = [];

      this.form.contributingPersonalCapital = undefined;
      this.form.personalCapitalPercentage = undefined;

      // Clear collateral types
      this.form.collateralTypes = [];

      // Clear financial information
      this.form.reportingPeriod = '';
      this.form.periodMonths = undefined;
      this.form.totalRevenue = undefined;
      this.form.customPeriodMonths = undefined;
      this.form.netProfit = undefined;
      this.form.depreciationAmortization = undefined;
      this.form.totalAssets = undefined;
      this.form.cashEquivalents = undefined;
      this.form.receivables = undefined;
      this.form.inventoryValue = undefined;
      this.form.currentLiabilities = undefined;
      this.form.shareholderEquity = undefined;
      this.form.alternativeNetWorth = undefined;
      this.form.existingLoansAmount = undefined;
      this.form.creditLinesLimit = undefined;
      this.form.monthlyDebtService = undefined;

      // Clear personal financial information
      this.form.totalPersonalIncome = undefined;
      this.form.personalNetWorth = undefined;
      this.form.personalLoansAmount = undefined;

      // Clear financial documentation
      this.form.financialDocumentation = [];

      // Clear legacy prescreening fields (if present)
      this.form.hasFiledAnnualReport = undefined;
      this.form.needsTechnicalAssistance = undefined;

      // Reset period selection state
      this.periodMonthsSelection = '';
    } else if (this.form.willFillPrescreening === true) {
      // Clear alternative info fields (shown when willFillPrescreening === false)
      // Note: creditScoreRange is NOT cleared - it's a common property that may be used in Solar section
      this.form.hasCoSigner = undefined;
      this.form.hasBusinessPlan = undefined;
      this.form.hasFinancialProjections = undefined;
    }

    this.saveFormData();
  }

  // Toggle between English and Spanish
  toggleLanguage(): void {
    this.currentLanguage = this.currentLanguage === 'en' ? 'es' : 'en';
    this.translateService.use(this.currentLanguage);
    // Save the current language to localStorage
    localStorage.setItem('formLanguage', this.currentLanguage);

    this.clarityService.trackEvent('outreach_language_changed', {
      outreach_language: this.currentLanguage,
    });
  }

  // Check if credit score range question should be shown in Solar and Energy section
  shouldShowCreditScoreInSolarSection(): boolean {
    // Show if user answered yes to any financing-related questions
    // Keep it visible even if already answered (allows user to see/change their answer)
    console.log(this.form.willFillPrescreening);
    return (
      (this.form.interestedInSolarProviderOrLoan === true ||
        this.form.interestedInBatteries === 'yes' ||
        this.form.lookingToAddElectricVehicle === true) &&
      (this.form.willFillPrescreening === undefined ||
        this.form.willFillPrescreening === true ||
        !this.form.interestedProducts.includes('loansAndCapital'))
    );
  }

  // Filter form data to only include relevant sections based on the navigation path
  private filterFormDataByNavigationPath(): GeneralOutreachFormV2 {
    const filteredForm: GeneralOutreachFormV2 = { ...this.form };
    const visitedSections = new Set(this.sectionHistory);

    // If they chose not to do detailed questionnaire, clear section 1+ data
    if (filteredForm.wantsDetailedQuestionnaire === false) {
      this.clearExistingBusinessFields(filteredForm);
    }

    // Clear section-specific data based on visited sections
    if (!visitedSections.has(1)) {
      this.clearExistingBusinessFields(filteredForm);
    }

    if (!visitedSections.has(2)) {
      this.clearLoanPrescreeningFields(filteredForm);
    }

    if (!visitedSections.has(3)) {
      this.clearAlternativeInfoFields(filteredForm);
    }

    // Clear conditional fields based on user responses
    this.clearConditionalFields(filteredForm);

    // Remove any undefined or empty values to clean up the submission
    this.removeEmptyValues(filteredForm);
    return filteredForm;
  }

  private clearExistingBusinessFields(form: GeneralOutreachFormV2): void {
    // Note: hasBusiness, businessName, businessAddress, businessWebsite, businessEstablished, numberOfEmployees
    // are all part of Section 0 (Basic Info) shown before the detailed questionnaire question, so we DON'T clear them
    // Only clear fields that are part of the detailed sections
    form.hasRegisteredEntity = undefined;
    form.willFillPrescreening = undefined;
  }

  private clearLoanPrescreeningFields(form: GeneralOutreachFormV2): void {
    form.primaryOwnerName = '';
    form.hasOtherBusinessInterests = undefined;
    form.numberOfOtherBusinesses = undefined;
    form.ownershipPercentage = undefined;
    form.loanAmount = undefined;
    form.loanTermMonths = undefined;
    form.contributingPersonalCapital = undefined;
    form.personalCapitalPercentage = undefined;

    // Reset ownership structure to initial empty state
    form.ownershipStructure = [
      {
        ownerName: '',
        ownershipPercentage: undefined,
        otherBusinessesPercentage: undefined,
      },
    ];

    // Reset project description
    form.projectDescription = [];

    // Reset collateral types
    form.collateralTypes = [];

    // Reset financial information
    form.reportingPeriod = '';
    form.periodMonths = undefined;
    form.customPeriodMonths = undefined;
    form.totalRevenue = undefined;
    form.netProfit = undefined;
    form.depreciationAmortization = undefined;
    form.totalAssets = undefined;
    form.cashEquivalents = undefined;
    form.receivables = undefined;
    form.inventoryValue = undefined;
    form.currentLiabilities = undefined;
    form.shareholderEquity = undefined;
    form.alternativeNetWorth = undefined;
    form.existingLoansAmount = undefined;
    form.creditLinesLimit = undefined;
    form.monthlyDebtService = undefined;
    form.totalPersonalIncome = undefined;
    form.personalNetWorth = undefined;
    form.personalLoansAmount = undefined;

    // Reset financial documentation
    form.financialDocumentation = [];
  }

  private clearAlternativeInfoFields(form: GeneralOutreachFormV2): void {
    const hasOnlyGrantPrograms =
      form.capitalProductType?.length === 1 &&
      form.capitalProductType.includes('grant_programs');
    const keepPrescreeningNoFields =
      form.interestedProducts?.includes('loansAndCapital') === true &&
      !hasOnlyGrantPrograms &&
      form.willFillPrescreening === false;

    if (!keepPrescreeningNoFields) {
      // Note: creditScoreRange is NOT cleared - it's a common property that may be used in Solar section
      form.hasCoSigner = undefined;
      form.hasBusinessPlan = undefined;
      form.hasFinancialProjections = undefined;
    }

    form.hasFiledAnnualReport = undefined;
    form.needsTechnicalAssistance = undefined;
    form.wantsToConnect = undefined;
    form.wantsFormCopy = undefined;
  }

  private clearConditionalFields(form: GeneralOutreachFormV2): void {
    // Clear other business interests count if user doesn't have other interests
    if (form.hasOtherBusinessInterests === false) {
      form.numberOfOtherBusinesses = undefined;
    }

    // Clear personal capital percentage if not contributing personal capital
    if (form.contributingPersonalCapital === false) {
      form.personalCapitalPercentage = undefined;
    }

    // Clear product-specific fields if product is not selected
    if (!form.interestedProducts.includes('loansAndCapital')) {
      this.clearLoansAndCapitalFields(form);
    }

    if (!form.interestedProducts.includes('technicalAssistance')) {
      this.clearTechnicalAssistanceFields(form);
    }

    if (!form.interestedProducts.includes('businessOperationalTools')) {
      this.clearBusinessToolsFields(form);
    }

    if (!form.interestedProducts.includes('commercialRealEstate')) {
      this.clearCommercialRealEstateFields(form);
    }

    if (!form.interestedProducts.includes('solarAndEnergy')) {
      this.clearSolarAndEnergyFields(form);
    }

    if (!form.interestedProducts.includes('findingInvestors')) {
      this.clearFindingInvestorsFields(form);
    }
  }

  private clearLoansAndCapitalFields(form: GeneralOutreachFormV2): void {
    form.capitalProductType = [];
    form.loanAmount = undefined;
    form.loanTermMonths = undefined;
    form.willFillPrescreening = undefined;

    // Clear prescreening fields
    form.ownershipStructure = [
      {
        ownerName: '',
        ownershipPercentage: undefined,
        otherBusinessesPercentage: undefined,
      },
    ];
    form.contributingPersonalCapital = undefined;
    form.personalCapitalPercentage = undefined;

    form.projectDescription = [];

    form.collateralTypes = [];

    // Clear financial information
    form.reportingPeriod = '';
    form.periodMonths = undefined;
    form.customPeriodMonths = undefined;
    form.totalRevenue = undefined;
    form.netProfit = undefined;
    form.depreciationAmortization = undefined;
    form.totalAssets = undefined;
    form.cashEquivalents = undefined;
    form.receivables = undefined;
    form.inventoryValue = undefined;
    form.currentLiabilities = undefined;
    form.shareholderEquity = undefined;
    form.alternativeNetWorth = undefined;
    form.existingLoansAmount = undefined;
    form.creditLinesLimit = undefined;
    form.monthlyDebtService = undefined;
    form.totalPersonalIncome = undefined;
    form.personalNetWorth = undefined;
    form.personalLoansAmount = undefined;

    form.financialDocumentation = [];

    // Clear alternative info fields (if not prescreening)
    // Note: creditScoreRange is NOT cleared here - it's a common property used by both loansAndCapital and solarAndEnergy
    form.hasBusinessPlan = undefined;
    form.hasFinancialProjections = undefined;
  }

  private clearTechnicalAssistanceFields(form: GeneralOutreachFormV2): void {
    form.businessIndustry = '';
    form.businessHelpAspects = [];
    form.workedWithSupportOrg = undefined;
    form.willingToPayForAssistance = '';
    form.wantsStartupAssistance = undefined;
    form.startupHelpAreas = [];
    form.interestedInSellingBusiness = '';
    form.interestedInEmployeeStrategies = '';
    form.interestedInSuccessionPlanning = undefined;
  }

  private clearBusinessToolsFields(form: GeneralOutreachFormV2): void {
    form.tools = {
      businessBanking: [],
      marketingHelp: [],
      techSupport: [],
      businessInsurance: [],
      newTechnology: [],
    };
  }

  private clearCommercialRealEstateFields(form: GeneralOutreachFormV2): void {
    form.lookingToRentOrBuy = '';
    form.lookingToUpgradeFacility = undefined;
    form.ownFacility = undefined;
    form.buildingOwnerWillingToSupport = '';
    form.interestedInEnergyUpgrades = undefined;
  }

  private clearSolarAndEnergyFields(form: GeneralOutreachFormV2): void {
    form.interestedInSolarProviderOrLoan = undefined;
    form.interestedInEEUpgrades = undefined;
    form.buildingBuiltYear = undefined;
    form.buildingImprovements = '';
    form.powerUtilityProviders = '';
    form.interestedInBatteries = '';
    form.canProvidePowerUseStatement = undefined;
    form.hasElectricVehicles = undefined;
    form.lookingToAddElectricVehicle = undefined;
  }

  private clearFindingInvestorsFields(form: GeneralOutreachFormV2): void {
    form.knowsInvestorType = '';
    form.investmentTypes = [];
    form.willingToRelocate = '';
  }

  // Clear term loan specific fields when leaving section 2 if loan type is working capital
  private clearTermLoanFieldsIfWorkingCapital(): void {
    // This method is no longer needed since loanType is removed
  }

  private removeEmptyValues(form: GeneralOutreachFormV2): void {
    // Remove properties with empty string values, null, or undefined (but keep zeros and false values)
    // Important: Always keep hasBusiness and business fields from page 0 since they're asked before detailed questionnaire
    Object.keys(form).forEach((key) => {
      const value = (form as any)[key];

      // Critical fields that should always be present - includes basic info and business fields from page 0
      const criticalFields = [
        'name',
        'email',
        'preferredLanguage',
        'isMilitaryVeteran',
        'isVeteranSpouse',
        'state',
        'city',
        'zipCode',
        'businessAddress',
        'consentToShare',
        'optOutCommunications',
        'otp',
        'hasBusiness', // Always keep hasBusiness as it's asked on page 0 before detailed questionnaire
      ];

      if (criticalFields.includes(key)) {
        return; // Don't remove these critical fields
      }

      if (value === '' || value === null || value === undefined) {
        delete (form as any)[key];
      }
    });

    // Clean up empty arrays
    if (form.projectDescription && form.projectDescription.length === 0) {
      delete (form as any).projectDescription;
    }

    if (form.collateralTypes && form.collateralTypes.length === 0) {
      delete (form as any).collateralTypes;
    }

    if (form.businessHelpAspects && form.businessHelpAspects.length === 0) {
      delete (form as any).businessHelpAspects;
    }

    if (form.capitalProductType && form.capitalProductType.length === 0) {
      delete (form as any).capitalProductType;
    }

    if (form.interestedProducts && form.interestedProducts.length === 0) {
      delete (form as any).interestedProducts;
    }

    if (form.tools) {
      const hasAnyTool = Object.values(form.tools).some(
        (category) => category.length > 0,
      );
      if (!hasAnyTool) {
        delete (form as any).tools;
      }
    }

    if (form.investmentTypes && form.investmentTypes.length === 0) {
      delete (form as any).investmentTypes;
    }

    if (
      form.financialDocumentation &&
      form.financialDocumentation.length === 0
    ) {
      delete (form as any).financialDocumentation;
    }

    if (form.startupHelpAreas && form.startupHelpAreas.length === 0) {
      delete (form as any).startupHelpAreas;
    }

    // Remove startupHelpAreas if hasBusiness is not 'no'
    if (form.hasBusiness !== 'no' && form.startupHelpAreas) {
      delete (form as any).startupHelpAreas;
    }

    // Remove wantsStartupAssistance if hasBusiness is not 'planning'
    if (
      form.hasBusiness !== 'planning' &&
      form.wantsStartupAssistance !== undefined
    ) {
      delete (form as any).wantsStartupAssistance;
    }

    // Remove ownershipStructure if it's empty or not filled
    if (form.ownershipStructure && form.ownershipStructure.length > 0) {
      const isOwnershipStructureEmpty = form.ownershipStructure.every((row) => {
        return (
          (!row.ownerName || row.ownerName.trim() === '') &&
          (row.ownershipPercentage === undefined ||
            row.ownershipPercentage === null) &&
          (row.otherBusinessesPercentage === undefined ||
            row.otherBusinessesPercentage === null)
        );
      });

      if (isOwnershipStructureEmpty) {
        delete (form as any).ownershipStructure;
      }
    }

    // Note: We preserve all numeric fields including 0 values as they may be valid user input
    // Only remove fields that are undefined, null, or empty strings
  }

  onPeriodMonthsChange(): void {
    // Update the numeric periodMonths based on selection
    if (this.periodMonthsSelection === '6') {
      this.form.periodMonths = 6;
      this.form.customPeriodMonths = undefined;
    } else if (this.periodMonthsSelection === '9') {
      this.form.periodMonths = 9;
      this.form.customPeriodMonths = undefined;
    } else if (this.periodMonthsSelection === '12') {
      this.form.periodMonths = 12;
      this.form.customPeriodMonths = undefined;
    } else if (this.periodMonthsSelection === 'other') {
      // When "other" is selected, periodMonths will be set from customPeriodMonths
      this.form.periodMonths = this.form.customPeriodMonths;
    } else {
      this.form.periodMonths = undefined;
      this.form.customPeriodMonths = undefined;
    }
    // Save form data when period months option changes
    this.saveFormData();
  }

  onCustomPeriodMonthsChange(): void {
    // When custom period months changes, update the main periodMonths field
    if (this.periodMonthsSelection === 'other') {
      this.form.periodMonths = this.form.customPeriodMonths;
    }
    this.saveFormData();
  }

  private initializePeriodMonthsSelection(): void {
    // Initialize periodMonthsSelection based on current periodMonths value
    if (this.form.periodMonths === 6) {
      this.periodMonthsSelection = '6';
    } else if (this.form.periodMonths === 9) {
      this.periodMonthsSelection = '9';
    } else if (this.form.periodMonths === 12) {
      this.periodMonthsSelection = '12';
    } else if (this.form.periodMonths && this.form.customPeriodMonths) {
      this.periodMonthsSelection = 'other';
    } else {
      this.periodMonthsSelection = '';
    }
  }

  toggleSection(
    section:
      | 'loansAndCapital'
      | 'technicalAssistance'
      | 'businessOperationalTools'
      | 'commercialRealEstate'
      | 'solarAndEnergy'
      | 'findingInvestors',
  ): void {
    this.sectionExpanded[section] = !this.sectionExpanded[section];
  }

  // Handle parent checkbox change for business operational tools
  /* eslint-disable @typescript-eslint/no-unused-vars */
  onParentCheckboxChange(_parent: string): void {
    // Parent checkboxes are now handled by the template directly
    // This method is kept for backward compatibility but does nothing
    this.saveFormData();
  }

  // Handle child checkbox change for business operational tools
  onChildCheckboxChange(_parent: string): void {
    // Child checkboxes are now handled by the template directly
    // This method is kept for backward compatibility but does nothing
    this.saveFormData();
  }
  /* eslint-enable @typescript-eslint/no-unused-vars */

  // Handle lookingToUpgradeFacility change for commercial real estate
  onLookingToUpgradeChange(value: boolean): void {
    this.form.lookingToUpgradeFacility = value;
    if (!value) {
      this.form.ownFacility = undefined;
      this.form.buildingOwnerWillingToSupport = '';
    }
    this.saveFormData();
  }

  // Handle ownFacility change for commercial real estate
  onOwnFacilityChange(value: boolean): void {
    this.form.ownFacility = value;
    if (value) {
      this.form.buildingOwnerWillingToSupport = '';
    }
    this.saveFormData();
  }

  // Handle interestedInEnergyUpgrades change
  onInterestedInEnergyUpgradesChange(value: boolean): void {
    this.form.interestedInEnergyUpgrades = value;
    // If user selects Yes, automatically check Solar and Energy Efficiency products
    // If user selects No, automatically uncheck it
    if (value === true) {
      if (!this.form.interestedProducts.includes('solarAndEnergy')) {
        this.form.interestedProducts.push('solarAndEnergy');
      }
    } else if (value === false) {
      const index = this.form.interestedProducts.indexOf('solarAndEnergy');
      if (index > -1) {
        this.form.interestedProducts.splice(index, 1);
      }
    }
    this.saveFormData();
  }

  // Handle Solar and Energy checkbox change
  onSolarAndEnergyCheckboxChange(checked: boolean): void {
    // If user unchecks Solar and Energy, also set interestedInEnergyUpgrades to false
    if (!checked) {
      this.form.interestedInEnergyUpgrades = false;
    }
    this.saveFormData();
  }

  // Handle Finding Investors checkbox change
  onFindingInvestorsCheckboxChange(): void {
    // Reset the auto-added flag when user manually changes the checkbox
    // This ensures that manual selections are preserved
    this.findingInvestorsAutoAdded = false;
    this.saveFormData();
  }

  // Handle Capital Product Type change
  onCapitalProductTypeChange(value: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    // Toggle the value in the array
    const index = this.form.capitalProductType.indexOf(value);
    if (isChecked && index === -1) {
      this.form.capitalProductType.push(value);
    } else if (!isChecked && index > -1) {
      this.form.capitalProductType.splice(index, 1);
    }

    // If user selects Venture Funding, automatically check Finding Investors
    if (value === 'venture_funding' && isChecked) {
      // Only mark as auto-added if it wasn't already selected
      if (!this.form.interestedProducts.includes('findingInvestors')) {
        this.findingInvestorsAutoAdded = true;
        this.form.interestedProducts.push('findingInvestors');
      }
    } else if (value === 'venture_funding' && !isChecked) {
      // If unchecking Venture Funding, only uncheck Finding Investors if it was auto-added
      if (this.findingInvestorsAutoAdded) {
        const findingInvestorsIndex =
          this.form.interestedProducts.indexOf('findingInvestors');
        if (findingInvestorsIndex > -1) {
          this.form.interestedProducts.splice(findingInvestorsIndex, 1);
        }
        this.findingInvestorsAutoAdded = false;
      }
    }

    // Check if only grant_programs is selected, clear prescreening fields if so
    const hasOnlyGrantPrograms =
      this.form.capitalProductType.length === 1 &&
      this.form.capitalProductType.includes('grant_programs');
    if (hasOnlyGrantPrograms) {
      // Clear willFillPrescreening and related fields
      this.form.willFillPrescreening = undefined;

      // Clear prescreening fields (shown when willFillPrescreening === true)
      this.form.ownershipStructure = [
        {
          ownerName: '',
          ownershipPercentage: undefined,
          otherBusinessesPercentage: undefined,
        },
      ];
      this.form.primaryOwnerName = '';
      this.form.hasOtherBusinessInterests = undefined;
      this.form.numberOfOtherBusinesses = undefined;
      this.form.ownershipPercentage = undefined;

      // Clear project description
      this.form.projectDescription = [];

      this.form.contributingPersonalCapital = undefined;
      this.form.personalCapitalPercentage = undefined;

      // Clear collateral types
      this.form.collateralTypes = [];

      // Clear financial information
      this.form.reportingPeriod = '';
      this.form.periodMonths = undefined;
      this.form.totalRevenue = undefined;
      this.form.customPeriodMonths = undefined;
      this.form.netProfit = undefined;
      this.form.depreciationAmortization = undefined;
      this.form.totalAssets = undefined;
      this.form.cashEquivalents = undefined;
      this.form.receivables = undefined;
      this.form.inventoryValue = undefined;
      this.form.currentLiabilities = undefined;
      this.form.shareholderEquity = undefined;
      this.form.alternativeNetWorth = undefined;
      this.form.existingLoansAmount = undefined;
      this.form.creditLinesLimit = undefined;
      this.form.monthlyDebtService = undefined;

      // Clear personal financial information
      this.form.totalPersonalIncome = undefined;
      this.form.personalNetWorth = undefined;
      this.form.personalLoansAmount = undefined;

      // Clear financial documentation
      this.form.financialDocumentation = [];

      // Clear alternative info fields (shown when willFillPrescreening === false)
      // Note: creditScoreRange is NOT cleared - it's a common property that may be used in Solar section
      this.form.hasCoSigner = undefined;
      this.form.hasBusinessPlan = undefined;
      this.form.hasFinancialProjections = undefined;

      // Reset period selection state
      this.periodMonthsSelection = '';
    }

    this.saveFormData();
  }

  // Get array of selected interest product sections in predefined order
  getSelectedSections(): string[] {
    // Define the desired order of sections
    const sectionOrder = [
      'loansAndCapital',
      'technicalAssistance',
      'businessOperationalTools',
      'commercialRealEstate',
      'solarAndEnergy',
      'findingInvestors',
    ];

    // Filter to only include selected products and return in predefined order
    return sectionOrder.filter((section) =>
      this.form.interestedProducts.includes(section),
    );
  }

  // Helper method to check if a product is selected
  isProductSelected(product: string): boolean {
    return this.form.interestedProducts.includes(product);
  }

  // Helper method to toggle product selection
  toggleProduct(product: string): void {
    const index = this.form.interestedProducts.indexOf(product);
    if (index > -1) {
      this.form.interestedProducts.splice(index, 1);
    } else {
      this.form.interestedProducts.push(product);
    }
    this.saveFormData();
  }

  // Helper method to check if a financial documentation is selected
  isFinancialDocSelected(doc: string): boolean {
    return this.form.financialDocumentation.includes(doc);
  }

  // Helper method to toggle financial documentation selection
  toggleFinancialDoc(doc: string): void {
    const index = this.form.financialDocumentation.indexOf(doc);
    if (index > -1) {
      this.form.financialDocumentation.splice(index, 1);
    } else {
      this.form.financialDocumentation.push(doc);
    }
    this.saveFormData();
  }

  // Get the section to display on the current page (for section 1+)
  getCurrentProductSection(): string | null {
    if (this.currentSection < 1) return null;

    const selectedSections = this.getSelectedSections();
    const sectionIndex = this.currentSection - 1;

    return selectedSections[sectionIndex] || null;
  }

  // Check if there are more product sections to show
  hasMoreProductSections(): boolean {
    const selectedSections = this.getSelectedSections();
    const sectionIndex = this.currentSection - 1;
    return sectionIndex < selectedSections.length - 1;
  }

  isBusinessEstablishedValid(): boolean {
    if (!this.form.businessEstablished) return true;

    const input = this.form.businessEstablished.trim();

    // If input is empty after trim, consider it valid (will be caught by required validation)
    if (!input) return true;

    // Check if it's a 4-digit year
    const yearPattern = /^\d{4}$/;
    if (yearPattern.test(input)) {
      const year = parseInt(input);
      const currentYear = new Date().getFullYear();
      return year >= 1900 && year <= currentYear;
    }

    // Check if it's a date in MM/DD/YYYY or MM-DD-YYYY format
    const datePattern =
      /^(0?[1-9]|1[0-2])[/-](0?[1-9]|[12]\d|3[01])[/-](\d{4})$/;
    if (datePattern.test(input)) {
      const match = input.match(datePattern);
      if (match) {
        const month = parseInt(match[1]);
        const day = parseInt(match[2]);
        const year = parseInt(match[3]);
        const date = new Date(year, month - 1, day);
        const currentDate = new Date();

        // Check if it's a valid date and not in the future
        return (
          date.getFullYear() === year &&
          date.getMonth() === month - 1 &&
          date.getDate() === day &&
          date <= currentDate &&
          year >= 1900
        );
      }
    }

    return false;
  }

  isBuildingBuiltYearValid(): boolean {
    if (!this.form.buildingBuiltYear) return true;

    const input = String(this.form.buildingBuiltYear).trim();

    if (!input) return true;

    // Check if it's a 4-digit year
    const yearPattern = /^\d{4}$/;
    if (yearPattern.test(input)) {
      const year = parseInt(input);
      const currentYear = new Date().getFullYear();
      return year >= 1900 && year <= currentYear;
    }

    // Check if it's a date in MM/DD/YYYY or MM-DD-YYYY format
    const datePattern =
      /^(0?[1-9]|1[0-2])[/-](0?[1-9]|[12]\d|3[01])[/-](\d{4})$/;
    if (datePattern.test(input)) {
      const match = input.match(datePattern);
      if (match) {
        const month = parseInt(match[1]);
        const day = parseInt(match[2]);
        const year = parseInt(match[3]);
        const date = new Date(year, month - 1, day);
        const currentDate = new Date();

        return (
          date.getFullYear() === year &&
          date.getMonth() === month - 1 &&
          date.getDate() === day &&
          date <= currentDate &&
          year >= 1900
        );
      }
    }

    return false;
  }

  isBuildingBuiltYearInFuture(): boolean {
    if (!this.form.buildingBuiltYear) return false;

    const input = String(this.form.buildingBuiltYear).trim();
    if (!input) return false;

    // Check if it's a 4-digit year
    const yearPattern = /^\d{4}$/;
    if (yearPattern.test(input)) {
      const year = parseInt(input);
      const currentYear = new Date().getFullYear();
      return year > currentYear;
    }

    // Check if it's a date in MM/DD/YYYY or MM-DD-YYYY format
    const datePattern =
      /^(0?[1-9]|1[0-2])[/-](0?[1-9]|[12]\d|3[01])[/-](\d{4})$/;
    if (datePattern.test(input)) {
      const match = input.match(datePattern);
      if (match) {
        const month = parseInt(match[1]);
        const day = parseInt(match[2]);
        const year = parseInt(match[3]);
        const date = new Date(year, month - 1, day);
        const currentDate = new Date();

        return (
          date.getFullYear() === year &&
          date.getMonth() === month - 1 &&
          date.getDate() === day &&
          date > currentDate
        );
      }
    }

    return false;
  }

  // Update form data whenever businessEstablished changes
  onBusinessEstablishedChange(): void {
    this.saveFormData();
  }

  // Helper method to check if a business help aspect is selected
  isBusinessHelpAspectSelected(aspect: string): boolean {
    return this.form.businessHelpAspects.includes(aspect);
  }

  // Helper method to toggle business help aspect selection
  toggleBusinessHelpAspect(aspect: string): void {
    const index = this.form.businessHelpAspects.indexOf(aspect);
    if (index > -1) {
      this.form.businessHelpAspects.splice(index, 1);
    } else {
      this.form.businessHelpAspects.push(aspect);
    }
    this.saveFormData();
  }

  // Helper method to check if an investment type is selected
  isInvestmentTypeSelected(type: string): boolean {
    return this.form.investmentTypes.includes(type);
  }

  // Helper method to toggle investment type selection
  toggleInvestmentType(type: string): void {
    const index = this.form.investmentTypes.indexOf(type);
    if (index > -1) {
      this.form.investmentTypes.splice(index, 1);
    } else {
      this.form.investmentTypes.push(type);
    }
    this.saveFormData();
  }

  // Generic helper method to toggle array values
  toggleArrayValue(array: string[], value: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const index = array.indexOf(value);
    if (checked && index === -1) {
      array.push(value);
    } else if (!checked && index > -1) {
      array.splice(index, 1);
    }
    this.saveFormData();
  }

  // Helper methods for business banking tools
  isBusinessBankingSelected(tool: string): boolean {
    return this.form.tools.businessBanking.includes(tool);
  }

  toggleBusinessBanking(tool: string): void {
    const index = this.form.tools.businessBanking.indexOf(tool);
    if (index > -1) {
      this.form.tools.businessBanking.splice(index, 1);
    } else {
      this.form.tools.businessBanking.push(tool);
    }
    this.saveFormData();
  }

  // Helper methods for marketing help tools
  isMarketingHelpSelected(tool: string): boolean {
    return this.form.tools.marketingHelp.includes(tool);
  }

  toggleMarketingHelp(tool: string): void {
    const index = this.form.tools.marketingHelp.indexOf(tool);
    if (index > -1) {
      this.form.tools.marketingHelp.splice(index, 1);
    } else {
      this.form.tools.marketingHelp.push(tool);
    }
    this.saveFormData();
  }

  // Helper methods for tech support tools
  isTechSupportSelected(tool: string): boolean {
    return this.form.tools.techSupport.includes(tool);
  }

  toggleTechSupport(tool: string): void {
    const index = this.form.tools.techSupport.indexOf(tool);
    if (index > -1) {
      this.form.tools.techSupport.splice(index, 1);
    } else {
      this.form.tools.techSupport.push(tool);
    }
    this.saveFormData();
  }

  // Helper methods for business insurance tools
  isBusinessInsuranceSelected(tool: string): boolean {
    return this.form.tools.businessInsurance.includes(tool);
  }

  toggleBusinessInsurance(tool: string): void {
    const index = this.form.tools.businessInsurance.indexOf(tool);
    if (index > -1) {
      this.form.tools.businessInsurance.splice(index, 1);
    } else {
      this.form.tools.businessInsurance.push(tool);
    }
    this.saveFormData();
  }

  // Helper methods for new technology tools
  isNewTechnologySelected(tool: string): boolean {
    return this.form.tools.newTechnology.includes(tool);
  }

  toggleNewTechnology(tool: string): void {
    const index = this.form.tools.newTechnology.indexOf(tool);
    if (index > -1) {
      this.form.tools.newTechnology.splice(index, 1);
    } else {
      this.form.tools.newTechnology.push(tool);
    }
    this.saveFormData();
  }

  // Check if business age and employee count combination seems unusual
  shouldShowBusinessAgeEmployeeAlert(): boolean {
    if (
      !this.form.businessEstablished ||
      this.form.numberOfEmployees === undefined
    ) {
      return false;
    }

    const businessAge = this.calculateBusinessAge(
      this.form.businessEstablished,
    );
    if (businessAge === null) {
      return false;
    }

    // Show alert if business age > 40 years and numberOfEmployees <= 1
    // OR if business age > 60 years and numberOfEmployees <= 2
    return (
      (businessAge > 40 && this.form.numberOfEmployees <= 1) ||
      (businessAge > 60 && this.form.numberOfEmployees <= 2)
    );
  }

  isNetWorthOrAlternativeValid(): boolean {
    return (
      this.isValidFinancialField('shareholderEquity') &&
      this.isValidFinancialField('alternativeNetWorth')
    );
  }

  // Ownership structure management methods
  addOwnershipRow(): void {
    // Initialize ownershipStructure if it doesn't exist
    if (!this.form.ownershipStructure) {
      this.form.ownershipStructure = [];
    }

    // Only allow adding if all current rows are properly filled
    if (this.canAddOwnershipRow()) {
      this.form.ownershipStructure.push({
        ownerName: '',
        ownershipPercentage: undefined,
        otherBusinessesPercentage: undefined,
      } as OwnershipRow);
      this.saveFormData();
    }
  }

  canAddOwnershipRow(): boolean {
    // If no ownership structure exists, allow adding
    if (
      !this.form.ownershipStructure ||
      this.form.ownershipStructure.length === 0
    ) {
      return true;
    }

    // Check if all existing rows have the required fields filled
    return this.form.ownershipStructure.every(
      (row) =>
        row.ownerName.trim() !== '' &&
        row.ownershipPercentage !== undefined &&
        row.ownershipPercentage !== null &&
        row.ownershipPercentage >= 0 &&
        row.ownershipPercentage <= 100 &&
        row.otherBusinessesPercentage !== undefined &&
        row.otherBusinessesPercentage !== null &&
        row.otherBusinessesPercentage >= 0 &&
        row.otherBusinessesPercentage <= 100,
    );
  }

  removeOwnershipRow(index: number): void {
    if (
      this.form.ownershipStructure &&
      this.form.ownershipStructure.length > 1
    ) {
      this.form.ownershipStructure.splice(index, 1);
      this.saveFormData();
    }
  }

  onOwnershipChange(): void {
    this.saveFormData();
  }

  // Clear business details when hasBusiness changes to "no" or "planning"
  onHasBusinessChange(): void {
    if (
      this.form.hasBusiness === 'no' ||
      this.form.hasBusiness === 'planning'
    ) {
      // Clear all business-related fields
      this.form.businessName = '';
      this.form.businessWebsite = '';
      this.form.businessType = '';
      this.form.businessEstablished = '';
      this.form.numberOfEmployees = undefined;
      this.form.hasRegisteredEntity = undefined;
    }
    this.saveFormData();
  }

  // Helper method to validate other businesses percentage for a specific row
  isOtherBusinessesPercentageValid(rowIndex: number): boolean {
    if (
      !this.form.ownershipStructure ||
      !this.form.ownershipStructure[rowIndex]
    ) {
      return true;
    }

    const row = this.form.ownershipStructure[rowIndex];
    // Check if the row has any data (ownerName or ownershipPercentage)
    const rowHasData = row.ownerName || row.ownershipPercentage !== undefined;

    if (!rowHasData) {
      return true; // Empty rows don't need validation
    }

    // If row has data, otherBusinessesPercentage is required
    if (
      row.otherBusinessesPercentage === undefined ||
      row.otherBusinessesPercentage === null
    ) {
      return false; // Required field for rows with data
    }
    return (
      row.otherBusinessesPercentage >= 0 &&
      row.otherBusinessesPercentage <= 100 &&
      this.isValidDecimal(row.otherBusinessesPercentage)
    );
  }

  isOwnershipStructureValid(): boolean {
    // If no ownership structure exists or is empty, it's invalid for section 2
    if (
      !this.form.ownershipStructure ||
      this.form.ownershipStructure.length === 0
    ) {
      return false;
    }

    // At least the first row must have owner name, ownership percentage, and other businesses percentage
    const firstRow = this.form.ownershipStructure[0];
    if (
      !firstRow ||
      !firstRow.ownerName ||
      firstRow.ownershipPercentage === undefined ||
      firstRow.ownershipPercentage === null ||
      !this.isValidDecimal(firstRow.ownershipPercentage) ||
      firstRow.otherBusinessesPercentage === undefined ||
      firstRow.otherBusinessesPercentage === null ||
      !this.isValidDecimal(firstRow.otherBusinessesPercentage)
    ) {
      return false;
    }

    // Validate all rows have required fields if they have any data
    return this.form.ownershipStructure.every((row) => {
      if (row.ownerName || row.ownershipPercentage !== undefined) {
        const ownershipValid =
          row.ownerName &&
          row.ownershipPercentage !== undefined &&
          row.ownershipPercentage !== null &&
          row.ownershipPercentage >= 0 &&
          row.ownershipPercentage <= 100 &&
          this.isValidDecimal(row.ownershipPercentage);

        // otherBusinessesPercentage is now required for rows with data
        const otherBusinessesValid =
          row.otherBusinessesPercentage !== undefined &&
          row.otherBusinessesPercentage !== null &&
          row.otherBusinessesPercentage >= 0 &&
          row.otherBusinessesPercentage <= 100 &&
          this.isValidDecimal(row.otherBusinessesPercentage);

        return ownershipValid && otherBusinessesValid;
      }
      return true; // Empty rows are valid
    });
  }

  isValidDecimal(value: number | undefined | null): boolean {
    if (value === undefined || value === null) return true;
    const parts = String(value).split('.');
    if (parts.length < 2) return true;
    return parts[1].length <= 2;
  }

  // State-County data management methods
  private loadStates(): void {
    this.addressService
      .getStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (states) => {
          this.states = (states || [])
            .slice()
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            );
        },
        error: (error) => {
          console.error('Error loading states:', error);
        },
      });
  }

  onCountyChange(): void {
    this.form.city = '';
    this.form.zipCode = '';
    this.availableZipCodes = [];
    this.saveFormData();
  }

  onCityChange(): void {
    const city = this.availableCities.find((c) => c.name === this.form.city);

    this.form.zipCode = '';
    this.availableZipCodes = [];

    if (!city) return;

    this.getCachedOrFetchZipcodes([city.id])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zips) => {
          this.availableZipCodes = zips || [];
        },
        error: (err) => {
          console.error('Error loading zipcodes:', err);
        },
      });

    this.saveFormData();
  }

  onStateChange(): void {
    this.form.county = '';
    this.form.city = '';
    this.form.zipCode = '';

    this.availableCities = [];
    this.availableCounties = [];
    this.availableZipCodes = [];

    const state = this.states.find((s) => s.name === this.form.state);
    if (!state) return;

    this.getCachedOrFetchCitiesAndCounties([state.id])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.availableCounties = (res.counties || [])
            .slice()
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            );
          this.availableCities = (res.cities || [])
            .slice()
            .sort((a, b) =>
              this.formatCityName(a.name).localeCompare(
                this.formatCityName(b.name),
                undefined,
                { sensitivity: 'base' },
              ),
            );
        },
        error: (err) => {
          console.error('Error loading cities & counties:', err);
        },
      });

    this.saveFormData();
  }

  private generateStateIdsCacheKey(stateIds: number[]): string {
    return `states_${stateIds.sort((a, b) => a - b).join('_')}`;
  }

  private generateCityIdsCacheKey(cityIds: number[]): string {
    return `cities_${cityIds.sort((a, b) => a - b).join('_')}`;
  }

  private getCachedOrFetchCitiesAndCounties(stateIds: number[]) {
    const cacheKey = this.generateStateIdsCacheKey(stateIds);
    const cached = this.citiesAndCountiesCache.get(cacheKey);

    if (cached) {
      return of(cached); // NO API CALL
    }

    return this.addressService.getCitiesAndCountiesByStateIds(stateIds).pipe(
      tap((res) => {
        this.citiesAndCountiesCache.set(cacheKey, res);
      }),
    );
  }

  private getCachedOrFetchZipcodes(cityIds: number[]) {
    const cacheKey = this.generateCityIdsCacheKey(cityIds);
    const cached = this.zipcodesCache.get(cacheKey);

    if (cached) {
      return of(cached);
    }

    return this.addressService.getZipcodesByCityIds(cityIds).pipe(
      tap((res) => {
        this.zipcodesCache.set(cacheKey, res);
      }),
    );
  }

  // Map form field names to human-readable display names
  private getFieldDisplayName(fieldName: string): string | null {
    const fieldDisplayNames: Record<string, string> = {
      // Basic Information (Section 0)
      name: 'Name',
      email: 'Email address',
      phone: 'Phone number',
      preferredLanguage: 'Preferred Language',
      isMilitaryVeteran: 'Are you a US Military Veteran?',
      isVeteranSpouse: 'Are you the spouse of a US military veteran?',
      state: 'State',
      county: 'County',
      city: 'Residential City',
      zipCode: 'ZIP Code',
      businessAddress:
        this.form?.hasBusiness !== 'yes'
          ? 'Address Line 1'
          : 'Business Address Line 1',
      hasBusiness: 'Do you have a business?',

      // Products of Interest
      interestedProducts: 'What products or services are you interested in?',
      'interestedProducts.generalAssistance': 'General Technical Assistance',
      'interestedProducts.businessLoan': 'Business Loan',
      'interestedProducts.commercialProperty': 'Commercial Property Loan',
      'interestedProducts.expansionPlanning': 'Expansion Planning',
      'interestedProducts.solar': 'Solar Improvements',
      'interestedProducts.buildingUpgrade': 'Building/Energy Upgrades',

      // Existing Business (Section 1)
      businessName: 'Business Name',
      businessWebsite: 'Business Website',
      businessType: 'Business Type',
      businessEstablished: 'Date (or year) your business has been established',
      numberOfEmployees: 'How many employees work for your business?',
      hasRegisteredEntity: 'Do you have a state-registered business entity?',
      wantsDetailedQuestionnaire:
        'Would you like to complete a detailed questionnaire?',
      willFillPrescreening: 'Will you fill out a prescreening application?',

      // Loan Prescreening (Section 2)
      ownershipStructure: 'Ownership Structure',
      primaryOwnerName: 'Primary Owner Name',
      hasOtherBusinessInterests: 'Do you have other business interests?',
      numberOfOtherBusinesses: 'Number of other businesses',
      ownershipPercentage: 'Ownership percentage',
      loanAmount: 'Loan amount',
      loanTermMonths: 'Loan term (months)',

      // Project Description
      projectDescription: 'Project Description',
      'projectDescription.businessAcquisition': 'Business Acquisition',
      'projectDescription.equipmentMaintenance': 'Equipment Maintenance',
      'projectDescription.businessExpansion': 'Business Expansion',
      'projectDescription.equipmentPurchase': 'Equipment Purchase',
      'projectDescription.realEstatePurchase': 'Real Estate Purchase',
      'projectDescription.realEstateConstruction': 'Real Estate Construction',
      'projectDescription.realEstateImprovements': 'Real Estate Improvements',
      'projectDescription.refinanceBusinessDebt': 'Refinance Business Debt',
      'projectDescription.refinanceOtherLoan': 'Refinance Other Loan',
      'projectDescription.solarImprovements': 'Solar Improvements',
      'projectDescription.startupActivity': 'Startup Activity',
      'projectDescription.vehiclePurchase': 'Vehicle Purchase',
      'projectDescription.purchaseInventory': 'Purchase Inventory',
      contributingPersonalCapital: 'Are you contributing personal capital?',
      personalCapitalPercentage: 'Personal capital percentage',
      collateralTypes: 'Collateral Types',
      'collateralTypes.realEstateBuilding': 'Real Estate/Building',
      'collateralTypes.land': 'Land',
      'collateralTypes.equipment': 'Equipment',
      'collateralTypes.vehicle': 'Vehicle',
      'collateralTypes.thirdPartyGuarantees': 'Third-Party Guarantees',
      'collateralTypes.residentialRealEstate': 'Residential Real Estate',
      'collateralTypes.inventory': 'Inventory',
      'collateralTypes.accountsReceivable': 'Accounts Receivable',
      'collateralTypes.cash': 'Cash',
      'collateralTypes.investmentAccounts': 'Investment Accounts',
      'collateralTypes.furnitureAndFixtures': 'Furniture and Fixtures',
      'collateralTypes.newEquipmentMachinery': 'New Equipment/Machinery',
      'collateralTypes.leaseholdImprovements': 'Leasehold Improvements',
      'collateralTypes.noCollateral': 'No Collateral',

      // Financial Information
      reportingPeriod: 'Reporting Period',
      periodMonths: 'Period (months)',
      customPeriodMonths: 'Custom period (months)',
      totalRevenue: 'Total Revenue',
      netProfit: 'Net Profit',
      depreciationAmortization: 'Depreciation/Amortization',
      totalAssets: 'Total Assets',
      cashEquivalents: 'Cash & Equivalents',
      receivables: 'Receivables',
      inventoryValue: 'Inventory Value',
      currentLiabilities: 'Current Liabilities',
      shareholderEquity: 'Shareholder Equity',
      alternativeNetWorth: 'Alternative Net Worth',
      existingLoansAmount: 'Existing Loans Amount',
      creditLinesLimit: 'Credit Lines Limit',
      monthlyDebtService: 'Monthly Debt Service',
      totalPersonalIncome: 'Total Personal Income',
      personalNetWorth: 'Personal Net Worth',
      personalLoansAmount: 'Personal Loans Amount',
      financialDocumentation: 'Financial Documentation',
      'financialDocumentation.auditedStatements':
        'Audited Financial Statements',
      'financialDocumentation.unauditedStatements':
        'Unaudited Financial Statements',
      'financialDocumentation.informalStatements':
        'Informal Financial Statements',
      'financialDocumentation.taxReturns': 'Tax Returns',
      'financialDocumentation.noStatements': 'No Financial Statements',
      'financialDocumentation.bankStatements': 'Bank Statements',

      // Alternative Info (Section 3)
      creditScoreRange: 'Credit Score Range',
      hasFiledAnnualReport:
        'Have you filed your Annual report within the state where your business is registered?',
      hasCoSigner: 'Do you have a co-signer?',
      needsTechnicalAssistance: 'Do you need technical assistance?',
      hasBusinessPlan: 'Do you have a business plan?',
      hasFinancialProjections: 'Do you have financial projections?',
      wantsToConnect: 'Do you want to connect?',
      wantsFormCopy: 'Do you want a copy of this form?',

      // Consent
      consentToShare: 'Consent to share information',
      optOutCommunications: 'Opt out of communications',

      // Email verification
      otp: 'Email verification code',
    };

    return fieldDisplayNames[fieldName] || null;
  }

  // Convert backend field names to frontend form field names
  private convertBackendFieldToFormField(backendField: string): string {
    // Convert snake_case to camelCase
    const formField = backendField.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );

    // Handle special cases for nested properties
    const fieldMappings: Record<string, string> = {
      interested_products_general_assistance:
        'interestedProducts.generalAssistance',
      interested_products_business_loan: 'interestedProducts.businessLoan',
      interested_products_commercial_property:
        'interestedProducts.commercialProperty',
      interested_products_expansion_planning:
        'interestedProducts.expansionPlanning',
      interested_products_solar: 'interestedProducts.solar',
      interested_products_building_upgrade:
        'interestedProducts.buildingUpgrade',
      project_description_business_acquisition:
        'projectDescription.businessAcquisition',
      project_description_equipment_maintenance:
        'projectDescription.equipmentMaintenance',
      project_description_business_expansion:
        'projectDescription.businessExpansion',
      project_description_equipment_purchase:
        'projectDescription.equipmentPurchase',
      project_description_real_estate_purchase:
        'projectDescription.realEstatePurchase',
      project_description_real_estate_construction:
        'projectDescription.realEstateConstruction',
      project_description_real_estate_improvements:
        'projectDescription.realEstateImprovements',
      project_description_refinance_business_debt:
        'projectDescription.refinanceBusinessDebt',
      project_description_refinance_other_loan:
        'projectDescription.refinanceOtherLoan',
      project_description_solar_improvements:
        'projectDescription.solarImprovements',
      project_description_startup_activity:
        'projectDescription.startupActivity',
      project_description_vehicle_purchase:
        'projectDescription.vehiclePurchase',
      project_description_purchase_inventory:
        'projectDescription.purchaseInventory',
      project_description_inventory_purchase:
        'projectDescription.purchaseInventory',
      collateral_types_real_estate_building:
        'collateralTypes.realEstateBuilding',
      collateral_types_residential_real_estate:
        'collateralTypes.residentialRealEstate',
      collateral_types_inventory: 'collateralTypes.inventory',
      collateral_types_accounts_receivable:
        'collateralTypes.accountsReceivable',
      collateral_types_cash: 'collateralTypes.cash',
      collateral_types_investment_accounts:
        'collateralTypes.investmentAccounts',
      collateral_types_furniture_and_fixtures:
        'collateralTypes.furnitureAndFixtures',
      collateral_types_new_equipment_machinery:
        'collateralTypes.newEquipmentMachinery',
      collateral_types_leasehold_improvements:
        'collateralTypes.leaseholdImprovements',
      collateral_types_land: 'collateralTypes.land',
      collateral_types_equipment: 'collateralTypes.equipment',
      collateral_types_vehicle: 'collateralTypes.vehicle',
      collateral_types_third_party_guarantees:
        'collateralTypes.thirdPartyGuarantees',
      collateral_types_no_collateral: 'collateralTypes.noCollateral',
      financial_documentation_audited_statements:
        'financialDocumentation.auditedStatements',
      financial_documentation_unaudited_statements:
        'financialDocumentation.unauditedStatements',
      financial_documentation_informal_statements:
        'financialDocumentation.informalStatements',
      financial_documentation_tax_returns: 'financialDocumentation.taxReturns',
      financial_documentation_no_statements:
        'financialDocumentation.noStatements',
      financial_documentation_bank_statements:
        'financialDocumentation.bankStatements',
      has_business: 'hasBusiness',
      business_name: 'businessName',
      business_address: 'businessAddress',
      business_website: 'businessWebsite',
      business_type: 'businessType',
      business_established: 'businessEstablished',
      number_of_employees: 'numberOfEmployees',
      has_registered_entity: 'hasRegisteredEntity',
      wants_detailed_questionnaire: 'wantsDetailedQuestionnaire',
      will_fill_prescreening: 'willFillPrescreening',
      ownership_structure: 'ownershipStructure',
      primary_owner_name: 'primaryOwnerName',
      has_other_business_interests: 'hasOtherBusinessInterests',
      number_of_other_businesses: 'numberOfOtherBusinesses',
      ownership_percentage: 'ownershipPercentage',
      loan_amount: 'loanAmount',
      loan_term_months: 'loanTermMonths',
      contributing_personal_capital: 'contributingPersonalCapital',
      personal_capital_percentage: 'personalCapitalPercentage',
      reporting_period: 'reportingPeriod',
      period_months: 'periodMonths',
      custom_period_months: 'customPeriodMonths',
      total_revenue: 'totalRevenue',
      net_profit: 'netProfit',
      depreciation_amortization: 'depreciationAmortization',
      total_assets: 'totalAssets',
      cash_equivalents: 'cashEquivalents',
      inventory_value: 'inventoryValue',
      current_liabilities: 'currentLiabilities',
      shareholder_equity: 'shareholderEquity',
      alternative_net_worth: 'alternativeNetWorth',
      existing_loans_amount: 'existingLoansAmount',
      credit_lines_limit: 'creditLinesLimit',
      monthly_debt_service: 'monthlyDebtService',
      total_personal_income: 'totalPersonalIncome',
      personal_net_worth: 'personalNetWorth',
      personal_loans_amount: 'personalLoansAmount',
      credit_score_range: 'creditScoreRange',
      has_co_signer: 'hasCoSigner',
      needs_technical_assistance: 'needsTechnicalAssistance',
      has_business_plan: 'hasBusinessPlan',
      has_financial_projections: 'hasFinancialProjections',
      wants_to_connect: 'wantsToConnect',
      wants_form_copy: 'wantsFormCopy',
      consent_to_share: 'consentToShare',
      opt_out_communications: 'optOutCommunications',
      preferred_language: 'preferredLanguage',
      is_military_veteran: 'isMilitaryVeteran',
      is_veteran_spouse: 'isVeteranSpouse',
      zip_code: 'zipCode',
    };

    return fieldMappings[backendField] || formField;
  }

  // Format field name as fallback when no display name mapping exists
  private formatFieldNameAsFallback(fieldName: string): string {
    // Convert camelCase to readable text
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .replace(/\./g, ' - '); // Replace dots with dashes for nested properties
  }

  private getDocumentStyles(): string {
    let styles = '';
    if (typeof document === 'undefined') return styles;
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        Array.from(sheet.cssRules || sheet.rules || []).forEach((rule) => {
          styles += rule.cssText + '\n';
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        // Silently handle CORS errors for external stylesheets (e.g., Google Fonts)
        if (sheet.href) styles += `@import url('${sheet.href}');\n`;
      }
    });
    document.querySelectorAll('style').forEach((style) => {
      styles += style.innerHTML + '\n';
    });
    return styles;
  }

  private getFormSnapshot(): { html: string; styles: string } {
    const elementSelector = '.form-container';
    const element = document.querySelector(elementSelector);
    if (!element) {
      console.error(`❌ Element not found: ${elementSelector}`);
      return { html: '', styles: '' };
    }
    // Deep clone the element
    const clone = element.cloneNode(true) as HTMLElement;
    // Populate form values into cloned DOM
    const inputs = element.querySelectorAll('input, textarea, select');
    const clonedInputs = clone.querySelectorAll('input, textarea, select');
    inputs.forEach((input: any, i) => {
      const cloneEl = clonedInputs[i] as any;
      if (!cloneEl) return;
      if (
        input instanceof HTMLInputElement ||
        input instanceof HTMLTextAreaElement
      ) {
        cloneEl.setAttribute('value', input.value);
        if (input instanceof HTMLTextAreaElement) {
          cloneEl.textContent = input.value;
          cloneEl.innerHTML = input.value;
        }
        if (
          (input.type === 'checkbox' || input.type === 'radio') &&
          input instanceof HTMLInputElement
        ) {
          if (input.checked) {
            cloneEl.setAttribute('checked', 'checked');
            cloneEl.checked = true;
          } else {
            cloneEl.removeAttribute('checked');
            cloneEl.checked = false;
          }
        }
      }
      if (input instanceof HTMLSelectElement) {
        Array.from(input.options).forEach((option, index) => {
          if (option.selected) {
            cloneEl.options[index].setAttribute('selected', 'selected');
          } else {
            cloneEl.options[index].removeAttribute('selected');
          }
        });
      }
    });

    return {
      html: clone.outerHTML,
      styles: '',
    };
  }

  private generateFormHtml(items: any[]) {
    if (items.length === 0) {
      return { html: '' };
    }

    const styleSet = new Set<string>();
    const parser = new DOMParser();

    // Collect document styles once for final email HTML generation
    const currentStyles = this.getDocumentStyles();
    if (currentStyles) {
      this.collectStyles(currentStyles, styleSet);
    }

    // Parse the first item as the base document
    const firstItem = items[0];
    const baseDoc = parser.parseFromString(firstItem.html, 'text/html');
    const baseFormElement = baseDoc.querySelector('form.general-form');

    if (!baseFormElement) {
      console.error('No form element found in first snapshot');
      return { html: '' };
    }

    // Process base document - remove unwanted elements
    this.removeUnwantedElements(baseDoc);

    // Collect styles from items if available
    for (const item of items) {
      if (item.styles) {
        this.collectStyles(item.styles, styleSet);
      }
    }

    // Process subsequent items - extract their form sections and append to base form
    for (let i = 1; i < items.length; i++) {
      const item = items[i];
      const doc = parser.parseFromString(item.html, 'text/html');

      // Remove unwanted elements
      this.removeUnwantedElements(doc);

      // Extract form sections from this page (only top-level sections)
      const formSections = Array.from(
        doc.querySelectorAll('form.general-form .form-section'),
      ).filter((section) => !section.parentElement?.closest('.form-section'));
      formSections.forEach((section) => {
        // Append section to the base form
        baseFormElement.appendChild(section.cloneNode(true));
      });
    }

    // Add uploaded files section if there are any attachments
    if (this.form.attachments && this.form.attachments.length > 0) {
      const uploadedFilesSection = baseDoc.createElement('div');
      uploadedFilesSection.style.cssText =
        'margin-top: 20px; padding: 15px; background-color: #f8f9fa; border: 1px solid #e2e8f0; border-radius: 8px;';
      uploadedFilesSection.innerHTML = `
        <h3 style="margin-top: 0; margin-bottom: 10px; color: #334ea5; font-size: 1.1rem;">Uploaded Documents</h3>
        <ul style="margin: 0; padding-left: 20px;">
          ${this.form.attachments.map((filename) => `<li style="margin-bottom: 5px; color: #333;">${filename}</li>`).join('')}
        </ul>
      `;
      baseFormElement.appendChild(uploadedFilesSection);
    }

    // Process textareas for email rendering: ensure text content is populated and styled
    baseDoc.querySelectorAll('textarea').forEach((ta) => {
      const textarea = ta as HTMLTextAreaElement;
      const val =
        textarea.textContent ||
        textarea.value ||
        textarea.getAttribute('value') ||
        '';
      textarea.textContent = val;
      textarea.innerHTML = val;
      textarea.style.cssText =
        'width: 100%; min-height: 100px; padding: 10px 12px; border: 1.5px solid #cbd5e1; border-radius: 8px; background-color: #f8fafc; color: #1e293b; font-size: 14px; font-family: inherit; line-height: 1.5; box-sizing: border-box; display: block; resize: none; white-space: pre-wrap; margin-top: 6px;';
    });

    // Process option cards (modern-option-card, radio-card, checkbox-card) for email compatibility
    const optionCards = baseDoc.querySelectorAll(
      '.modern-option-card, .modern-checkbox-card, .radio-card, .checkbox-card, label.option-card',
    );
    optionCards.forEach((cardNode) => {
      const card = cardNode as HTMLElement;
      const input = card.querySelector(
        'input[type="checkbox"], input[type="radio"]',
      ) as HTMLInputElement | null;
      const isSelected =
        card.classList.contains('selected') ||
        !!card.querySelector('input:checked') ||
        (input && (input.checked || input.hasAttribute('checked')));
      const isCheckbox =
        card.classList.contains('modern-checkbox-card') ||
        card.classList.contains('checkbox-card') ||
        (input && input.type === 'checkbox');

      // Style card container as email-safe table
      card.style.cssText =
        'display: table; width: 100%; border: 2px solid ' +
        (isSelected ? '#334ea5' : '#e2e8f0') +
        '; border-radius: 8px; padding: 12px 16px; margin-bottom: 10px; background-color: ' +
        (isSelected ? '#f0f7ff' : '#ffffff') +
        '; box-sizing: border-box; text-decoration: none;';

      // Style content area
      const content = card.querySelector(
        '.option-content',
      ) as HTMLElement | null;
      if (content) {
        content.style.cssText =
          'display: table-cell; vertical-align: middle; width: 100%; padding-right: 12px;';
      }
      const title = card.querySelector('.option-title') as HTMLElement | null;
      if (title) {
        title.style.cssText =
          'font-size: 15px; font-weight: 600; color: ' +
          (isSelected ? '#334ea5' : '#1e293b') +
          '; line-height: 1.4; margin: 0;';
      }
      const desc = card.querySelector(
        '.option-description',
      ) as HTMLElement | null;
      if (desc) {
        desc.style.cssText =
          'font-size: 13px; color: #64748b; line-height: 1.4; margin-top: 2px;';
      }

      // Style icon if present
      const icon = card.querySelector('.option-icon') as HTMLElement | null;
      if (icon) {
        icon.style.cssText =
          'display: table-cell; vertical-align: middle; width: 44px; padding-right: 12px; font-size: 1.25rem; text-align: center;';
      }

      // Process checkmark element
      let checkmark = card.querySelector(
        '.option-checkmark, .radio-checkmark, .checkbox-checkmark, .email-checkmark-cell',
      ) as HTMLElement | null;
      if (!checkmark) {
        checkmark = baseDoc.createElement('div');
        card.appendChild(checkmark);
      }
      checkmark.className = 'email-checkmark-cell';

      checkmark.style.cssText =
        'display: table-cell; vertical-align: middle; text-align: right; width: 24px; min-width: 24px; border: none !important; background: transparent !important; border-radius: 0 !important; box-shadow: none !important; padding: 0; margin: 0;';

      if (isSelected) {
        if (isCheckbox) {
          checkmark.innerHTML =
            '<span style="display:inline-block; width:20px; height:20px; border-radius:4px; background-color:#334ea5; border:2px solid #334ea5; color:#ffffff; font-weight:bold; font-size:14px; line-height:20px; text-align:center; box-sizing:border-box; vertical-align:middle;">✓</span>';
        } else {
          checkmark.innerHTML =
            '<span style="display:inline-block; width:20px; height:20px; border-radius:50%; background-color:#334ea5; border:2px solid #334ea5; color:#ffffff; font-size:10px; line-height:20px; text-align:center; box-sizing:border-box; vertical-align:middle;">●</span>';
        }
      } else {
        if (isCheckbox) {
          checkmark.innerHTML =
            '<span style="display:inline-block; width:20px; height:20px; border-radius:4px; background-color:#ffffff; border:2px solid #cbd5e1; box-sizing:border-box; vertical-align:middle;"></span>';
        } else {
          checkmark.innerHTML =
            '<span style="display:inline-block; width:20px; height:20px; border-radius:50%; background-color:#ffffff; border:2px solid #cbd5e1; box-sizing:border-box; vertical-align:middle;"></span>';
        }
      }

      // Remove native input inside card node so it never gets rendered inside or alongside checkmark in email
      if (input) {
        input.remove();
      }
    });

    // Style standard radio/checkbox inputs outside option cards
    baseDoc
      .querySelectorAll('input[type="radio"], input[type="checkbox"]')
      .forEach((inp) => {
        const input = inp as HTMLInputElement;
        if (
          !input.closest(
            '.modern-option-card, .modern-checkbox-card, .radio-card, .checkbox-card',
          )
        ) {
          input.style.cssText =
            'display: inline-block; width: 16px; height: 16px; margin-right: 6px; vertical-align: middle; opacity: 1; position: static; cursor: default;';
          if (input.parentElement) {
            input.parentElement.style.cssText =
              'display: inline-block; margin-right: 20px; font-size: 14px; color: #1e293b; font-weight: 500; cursor: default; vertical-align: middle;';
          }
        } else {
          input.remove();
        }
      });

    // Disable all form controls
    const elementsToDisable = baseDoc.querySelectorAll(
      'input, textarea, select, button',
    );
    elementsToDisable.forEach((el) => {
      const input = el as
        | HTMLInputElement
        | HTMLTextAreaElement
        | HTMLSelectElement
        | HTMLButtonElement;

      input.disabled = true;
      if (!input.style.display || input.style.display === '') {
        input.style.opacity = '1';
      }
      input.style.color = '#000';
      input.style.filter = 'grayscale(0%)';
      input.style.cursor = 'default';
    });

    // Remove contenteditable from all elements
    const editableElements = baseDoc.querySelectorAll('[contenteditable]');
    editableElements.forEach((el) => el.removeAttribute('contenteditable'));

    // Remove inline event handlers
    baseDoc.querySelectorAll('*').forEach((el) => {
      const element = el as HTMLElement;
      Array.from(element.attributes).forEach((attr) => {
        if (/^on/i.test(attr.name)) {
          element.removeAttribute(attr.name);
        }
      });
    });

    // Get final HTML
    let finalBody = baseDoc.body.innerHTML;
    finalBody = finalBody
      .replace(/\s*_ng(content|host)-[^\s>]*/gi, '')
      .replace(/\s*ng-reflect-[a-zA-Z-]+="[^"]*"/g, '');

    // Replace classes with inline styles for email compatibility
    finalBody = this.replaceClassesWithInlineStyles(finalBody);

    const finalStyles = Array.from(styleSet).join('\n');
    const cleanedStyles = this.replaceUnsupportedCss(finalStyles);
    const inlinedHtml = juice.inlineContent(finalBody, cleanedStyles, {
      preserveMediaQueries: false,
    });

    return {
      html: inlinedHtml,
    };
  }

  private removeUnwantedElements(doc: Document): void {
    // Remove various unwanted elements
    const selectorsToRemove = [
      '.form-actions',
      '.language-toggle-container',
      '.actions-cell',
      '.add-button',
      '.mobile-scroll-hint',
      '.remove-button',
      '.file-upload-section',
    ];

    selectorsToRemove.forEach((selector) => {
      doc.querySelectorAll(selector).forEach((el) => el.remove());
    });

    // Remove specific "Actions" table-cell headers
    const headers = doc.querySelectorAll('.table-header');
    headers.forEach((header) => {
      const cells = header.querySelectorAll('.header-cell');
      if (cells[3]) {
        cells[3].remove(); // ACTIONS column
      }
    });
  }

  private collectStyles(styles: string, styleSet: Set<string>): void {
    const cleanedStyle = styles
      .replace(/<style[^>]*>/gi, '')
      .replace(/<\/style>/gi, '')
      .replace(/\[_ng(content|host)-[^\]]*\]/gi, '')
      .trim();

    const lines = cleanedStyle.split('\n');

    for (const line of lines) {
      const trimmedLine = line.trim();

      if (!trimmedLine.includes('{') || !trimmedLine.includes('}')) {
        continue;
      }

      if (
        trimmedLine.includes('.error-message-container') ||
        trimmedLine.includes('.table-container')
      ) {
        continue;
      }

      if (
        trimmedLine.includes('.error-message') ||
        trimmedLine.includes('...') ||
        !/^[a-zA-Z.#[\]:\-_\s\d,>~+()*='"\\]+{/.test(trimmedLine)
      ) {
        continue;
      }

      try {
        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(trimmedLine);
        styleSet.add(trimmedLine);
      } catch {
        console.warn('Invalid CSS skipped:', trimmedLine);
      }
    }
  }

  private replaceClassesWithInlineStyles(html: string): string {
    let result = html;

    // Replace .table-container with Gmail-safe inline style
    result = result.replace(
      /class\s*=\s*["']?table-container["']?/g,
      'style="border: 1px solid #ccc; padding: 10px; margin-bottom: 20px; background-color: #f9f9f9;"',
    );

    // Replace .ownership-table with table structure
    result = result.replace(
      /class\s*=\s*["']?ownership-table["']?/g,
      'style="display: table; width: 100%; border-collapse: collapse; border: 1px solid #ddd;"',
    );

    // Replace .table-header
    result = result.replace(
      /class\s*=\s*["']?table-header["']?/g,
      'style="display: table-row; background-color: #e0e0e0; font-weight: bold;"',
    );

    // Replace .table-row
    result = result.replace(
      /class\s*=\s*["']?table-row["']?/g,
      'style="display: table-row;"',
    );

    // Replace .table-cell and .header-cell
    result = result.replace(
      /class\s*=\s*["']?(table-cell|header-cell)["']?/g,
      'style="display: table-cell; padding: 8px; border: 1px solid #ddd; vertical-align: top; color: #333;"',
    );

    // Replace .actions-cell
    result = result.replace(/class\s*=\s*["']?actions-cell["']?/g, '');

    return result;
  }

  replaceUnsupportedCss(styles: string): string {
    // Override mobile media queries with desktop ones
    const withoutMediaQueries = styles.replace(
      /@media\s*\(max-width\s*:\s*480px\)/gi,
      '@media (min-width: 769px)',
    );

    // Or alternatively — if you'd prefer to REMOVE media queries altogether:
    // withoutMediaQueries = withoutMediaQueries.replace(/@media[^{]+\{([\s\S]+?})\s*}/gi, '');

    const cleanedStyles = withoutMediaQueries
      .replace(/\.option-checkmark\s*{[^}]*}/gi, '')
      .replace(/\.table-header\s*{\s*display\s*:\s*none\s*;?\s*}/gi, '')
      .replace(/display:\s*flex/g, 'display: block')
      .replace(/display:\s*inline-flex/g, 'display: inline-block')
      .replace(/float:\s*(left|right)/g, 'display: inline-block')
      .replace(/position:\s*absolute/g, '')
      .replace(/position:\s*relative/g, '')
      .replace(/visibility:\s*hidden/g, 'display: none');

    const paddingRule = `.table-header { padding: 0.5rem 8px }\n`;

    return paddingRule + cleanedStyles;
  }

  // Validate financial field - accepts numbers or empty values
  isValidFinancialField(fieldName: string): boolean {
    const value = (this.form as any)[fieldName];
    return this.validateFieldWithConstraint(fieldName, value);
  }

  // Helper method to check if a financial field value is valid for validation
  private isValidFinancialValue(value: number | undefined | null): boolean {
    if (value === undefined || value === null) {
      return true;
    }
    return !isNaN(value) && value >= 0;
  }

  // Check if personal income warning should be displayed
  isPersonalIncomeWarningVisible(): boolean {
    const totalRevenue = this.form.totalRevenue;
    const totalPersonalIncome = this.form.totalPersonalIncome;

    // Only show warning if both fields have values
    if (
      !totalRevenue ||
      !totalPersonalIncome ||
      totalRevenue === undefined ||
      totalPersonalIncome === undefined
    ) {
      return false;
    }

    // Show warning if personal income is >= total revenue and both are valid numbers > 0
    return (
      !isNaN(totalRevenue) &&
      !isNaN(totalPersonalIncome) &&
      totalRevenue > 0 &&
      totalPersonalIncome > 0 &&
      totalPersonalIncome >= totalRevenue
    );
  }

  // Check if personal net worth warning should be displayed
  isPersonalNetWorthWarningVisible(): boolean {
    const shareholderEquity = this.form.shareholderEquity;
    const alternativeNetWorth = this.form.alternativeNetWorth;
    const personalNetWorth = this.form.personalNetWorth;

    // Only show warning if personal net worth has a value
    if (personalNetWorth === undefined || personalNetWorth === null) {
      return false;
    }

    const personalNetWorthNum =
      typeof personalNetWorth === 'number'
        ? personalNetWorth
        : typeof personalNetWorth === 'string'
          ? parseFloat(personalNetWorth)
          : 0;

    // Check against shareholder equity
    let showWarningForShareholderEquity = false;
    if (shareholderEquity && shareholderEquity !== undefined) {
      showWarningForShareholderEquity =
        !isNaN(shareholderEquity) &&
        shareholderEquity > 0 &&
        !isNaN(personalNetWorthNum) &&
        personalNetWorthNum > 0 &&
        personalNetWorthNum >= shareholderEquity;
    }

    // Check against alternative net worth
    let showWarningForAlternativeNetWorth = false;
    if (alternativeNetWorth && alternativeNetWorth !== undefined) {
      showWarningForAlternativeNetWorth =
        !isNaN(alternativeNetWorth) &&
        alternativeNetWorth > 0 &&
        !isNaN(personalNetWorthNum) &&
        personalNetWorthNum > 0 &&
        personalNetWorthNum >= alternativeNetWorth;
    }

    // Show warning if personal net worth >= either shareholder equity or alternative net worth
    return showWarningForShareholderEquity || showWarningForAlternativeNetWorth;
  }

  // Check if monthly debt service warning should be displayed
  isMonthlyDebtServiceWarningVisible(): boolean {
    const existingLoansAmount = this.form.existingLoansAmount;
    const monthlyDebtService = this.form.monthlyDebtService;

    // Only show warning if both fields have values
    if (
      !existingLoansAmount ||
      !monthlyDebtService ||
      existingLoansAmount === undefined ||
      monthlyDebtService === undefined
    ) {
      return false;
    }

    // Check if existing loans > 1000 and monthly debt service < max(100, existingLoans/240)
    if (
      !isNaN(existingLoansAmount) &&
      !isNaN(monthlyDebtService) &&
      existingLoansAmount > 1000
    ) {
      const minimumExpectedPayment = Math.max(100, existingLoansAmount / 240);
      return monthlyDebtService < minimumExpectedPayment;
    }

    return false;
  }

  // Helper method to check if a project description option is selected
  isProjectDescriptionSelected(value: string): boolean {
    return this.form.projectDescription.includes(value);
  }

  // Helper method to toggle project description option
  toggleProjectDescription(value: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    const index = this.form.projectDescription.indexOf(value);

    if (isChecked && index === -1) {
      this.form.projectDescription.push(value);
    } else if (!isChecked && index > -1) {
      this.form.projectDescription.splice(index, 1);
    }

    this.saveFormData();
  }

  // Helper method to check if a collateral type is selected
  isCollateralTypeSelected(value: string): boolean {
    return this.form.collateralTypes.includes(value);
  }

  // Helper method to toggle collateral type
  toggleCollateralType(value: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    const index = this.form.collateralTypes.indexOf(value);

    if (isChecked && index === -1) {
      this.form.collateralTypes.push(value);
    } else if (!isChecked && index > -1) {
      this.form.collateralTypes.splice(index, 1);
    }

    this.saveFormData();
  }

  // File upload methods
  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    this.fileUploadError = '';

    // Process each selected file
    const filesToAdd: File[] = [];
    for (const file of Array.from(input.files)) {
      // Check if we've reached max files
      if (this.selectedFiles.length + filesToAdd.length >= 3) {
        this.fileUploadError = 'Maximum 3 files can be uploaded.';
        break;
      }

      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        this.fileUploadError = validation.error || '';
        continue; // Skip this file but process others
      }

      // Check for duplicate file names
      if (
        this.selectedFiles.some((f) => f.name === file.name) ||
        filesToAdd.some((f) => f.name === file.name)
      ) {
        this.fileUploadError = `A file named "${file.name}" has already been selected.`;
        continue; // Skip this file but process others
      }

      filesToAdd.push(file);
    }

    // Add all valid files to selected files
    this.selectedFiles.push(...filesToAdd);
    input.value = ''; // Reset input for next selection
    this.saveFormData();
  }

  validateFile(file: File): { valid: boolean; error?: string } {
    const maxSize = 20 * 1024 * 1024; // 20MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];

    // Check max files
    if (this.selectedFiles.length >= 3) {
      return {
        valid: false,
        error: 'Maximum 3 files can be uploaded.',
      };
    }

    // Check file size
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size cannot exceed 20MB. ${file.name} is ${this.formatFileSize(file.size)}.`,
      };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error:
          'Only PDF, Word documents, Excel files, and images (JPEG, PNG) are allowed.',
      };
    }

    return { valid: true };
  }

  removeFile(fileName: string): void {
    this.selectedFiles = this.selectedFiles.filter((f) => f.name !== fileName);
    this.fileUploadError = '';
    // Reset consent when all files are removed
    if (this.selectedFiles.length === 0) {
      this.fileUploadConsent = false;
      this.termsOfUseConsent = false;
    }
    this.saveFormData();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  // Upload files to Azure Blob Storage
  async uploadFilesToAzure(): Promise<void> {
    this.allUploadsCompleted = false;

    for (const upload of this.fileUploads) {
      try {
        const file = this.selectedFiles.find((f) => f.name === upload.fileName);
        if (!file) {
          upload.status = 'error';
          upload.error = 'File not found';
          continue;
        }

        // Upload to Azure Blob Storage using SAS URL
        const blockBlobClient = new BlockBlobClient(upload.sasUrl);

        await blockBlobClient.uploadData(file, {
          onProgress: (progress) => {
            upload.progress = Math.round(
              (progress.loadedBytes / file.size) * 100,
            );
          },
        });

        upload.progress = 100;
        upload.status = 'completed';

        // Update file upload status on backend
        await this.outreachService
          .updateFileUploadStatus(this.submittedFormId, upload.fileId)
          .toPromise();
      } catch (error: any) {
        console.error('Error uploading file:', error);
        upload.status = 'error';
        upload.error = error.message || 'Upload failed';
        this.clarityService.sendClarityEvent('outreach_file_upload_failed');
      }
    }

    // Check if all uploads completed successfully
    this.allUploadsCompleted = this.fileUploads.every(
      (u) => u.status === 'completed',
    );

    // Auto-redirect after 2 seconds if all uploads completed
    if (this.allUploadsCompleted) {
      this.clarityService.trackEvent('outreach_file_upload_completed', {
        outreach_file_count: String(this.fileUploads.length),
      });
      setTimeout(() => {
        this.finishUploadAndShowSuccess();
      }, 2000);
    }
  }

  finishUploadAndShowSuccess(): void {
    this.showFileUploadProgress = false;
    this.isSubmitted = true;
    this.showFeedbackPopupAfterDelay();
    this.startPollingMatchedConnections();
  }

  startPollingMatchedConnections(): void {
    if (!this.submittedFormId || !this.submittedJobId) {
      return;
    }

    this.isPollingMatches = true;
    this.pollingAttempts = 0;
    this.pollingMessage = this.translateService.instant(
      'GENERAL_OUTREACH_FORM.MESSAGES.POLLING_STATUS_2',
    );

    // Clear any existing polling interval
    this.stopPollingMatchedConnections();

    // Start polling
    this.pollingInterval = setInterval(() => {
      this.pollingAttempts++;

      if (this.pollingAttempts >= this.MAX_POLLING_ATTEMPTS) {
        this.stopPollingMatchedConnections();
        this.pollingMessage = this.translateService.instant(
          'GENERAL_OUTREACH_FORM.MESSAGES.POLLING_ERROR_MAX_ATTEMPTS',
        );
        return;
      }

      this.outreachService
        .getMatchedConnections(this.submittedFormId, this.submittedJobId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.status === 3) {
              // Matching completed
              this.matchedConnections = response.matched_connections || [];
              this.stopPollingMatchedConnections();
              this.isPollingMatches = false;
              this.pollingMessage = '';
            } else {
              // Update polling message based on status
              this.updatePollingMessage(response.status);
            }
          },
          error: (error) => {
            console.error('Error polling matched connections:', error);
            // Continue polling on error unless max attempts reached
            if (this.pollingAttempts >= this.MAX_POLLING_ATTEMPTS) {
              this.stopPollingMatchedConnections();
              this.isPollingMatches = false;
              this.pollingMessage = this.translateService.instant(
                'GENERAL_OUTREACH_FORM.MESSAGES.POLLING_ERROR_RETRY',
              );
            }
          },
        });
    }, this.POLLING_INTERVAL_MS);
  }

  stopPollingMatchedConnections(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  updatePollingMessage(status: number): void {
    switch (status) {
      case 0:
        this.pollingMessage = this.translateService.instant(
          'GENERAL_OUTREACH_FORM.MESSAGES.POLLING_STATUS_0',
        );
        break;
      case 1:
        this.pollingMessage = this.translateService.instant(
          'GENERAL_OUTREACH_FORM.MESSAGES.POLLING_STATUS_1',
        );
        break;
      case 2:
        this.pollingMessage = this.translateService.instant(
          'GENERAL_OUTREACH_FORM.MESSAGES.POLLING_STATUS_2',
        );
        break;
      default:
        this.pollingMessage = this.translateService.instant(
          'GENERAL_OUTREACH_FORM.MESSAGES.POLLING_STATUS_DEFAULT',
        );
    }
  }

  formatCityName(name: string): string {
    if (!name) return '';

    return name
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatCityItem = (item: City | any): string => {
    const name = typeof item === 'string' ? item : item?.name || '';
    return this.formatCityName(name);
  };

  getCountyPlaceholder(): string {
    if (!this.form.state) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_STATE_FIRST',
      );
    }
    if (this.availableCounties.length === 0) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_COUNTIES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_COUNTY',
    );
  }

  getCityPlaceholder(): string {
    if (!this.form.state) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_STATE_FIRST',
      );
    }
    if (this.availableCities.length === 0) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_CITIES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_CITY',
    );
  }

  getZipPlaceholder(): string {
    if (!this.form.city) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_CITY_FIRST',
      );
    }
    if (this.availableZipCodes.length === 0) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_ZIPCODES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_ZIP',
    );
  }
}
